import React, { useState } from "react";
import "../../css/terms.scss";
import Header from "./Header";
import Footer from "./Footer";
import { Link } from "react-router-dom";

const Terms = () => {
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [tocOpen, setTocOpen] = useState(false);
  const linkListRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const linkList = linkListRef.current;
    const container = containerRef.current;
    if (!linkList || !container) return;

    const topOffset = 12; // matches CSS top for fixed state

    let initialTop = linkList.getBoundingClientRect().top + window.scrollY;

    function recalc() {
      initialTop = linkList.getBoundingClientRect().top + window.scrollY;
      onScroll();
    }

    function onScroll() {
      if (window.innerWidth > 768) {
        linkList.classList.remove("fixed");
        linkList.style.left = "";
        linkList.style.width = "";
        return;
      }
      const scrollY = window.scrollY || window.pageYOffset;
      if (scrollY + topOffset > initialTop) {
        if (!linkList.classList.contains("fixed")) {
          linkList.classList.add("fixed");
          // align fixed link-list to container
          const rect = container.getBoundingClientRect();
          linkList.style.left = `${rect.left}px`;
          linkList.style.width = `${rect.width}px`;
        }
      } else {
        if (linkList.classList.contains("fixed")) {
          linkList.classList.remove("fixed");
          linkList.style.left = "";
          linkList.style.width = "";
        }
      }
    }

    // 鼠标悬停在 TOC 上时，使用 wheel 事件让 TOC 自行滚动，避免页面滚动
    function onWheel(e) {
      if (!linkList) return;
      // target the inner scrollable list (ul.toc-list)
      const scrollEl =
        linkList.querySelector &&
        (linkList.querySelector(".toc-list") || linkList.querySelector("ul"));
      if (!scrollEl) return;

      const canScroll = scrollEl.scrollHeight > scrollEl.clientHeight;
      if (!canScroll) return;

      const delta = e.deltaY;
      const atTop = scrollEl.scrollTop === 0;
      const atBottom =
        Math.abs(
          scrollEl.scrollTop + scrollEl.clientHeight - scrollEl.scrollHeight
        ) < 1;

      // If trying to scroll up at top or down at bottom, allow page to scroll
      if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
        return;
      }

      // Prevent page scroll and scroll the inner element
      e.preventDefault();
      scrollEl.scrollTop += delta;
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", recalc);
    // attach wheel handler directly to inner scroll element if present
    const scrollEl =
      linkList.querySelector(".toc-list") || linkList.querySelector("ul");
    if (scrollEl) {
      // ensure focusable for some browsers
      scrollEl.setAttribute("tabindex", "0");
      scrollEl.addEventListener("wheel", onWheel, { passive: false });
    } else {
      linkList.addEventListener("wheel", onWheel, { passive: false });
    }
    // initial
    recalc();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", recalc);
      const scrollElCleanup =
        linkList.querySelector(".toc-list") || linkList.querySelector("ul");
      if (scrollElCleanup) {
        scrollElCleanup.removeEventListener("wheel", onWheel, {
          passive: false,
        });
      } else {
        linkList.removeEventListener("wheel", onWheel, { passive: false });
      }
      if (linkList) {
        linkList.classList.remove("fixed");
        linkList.style.left = "";
        linkList.style.width = "";
      }
    };
  }, []);
  return (
    <div>
      <Header />
      <main className="terms-main">
        <section
          id="hero"
          className="large-and-concise block_78c56d14b2728a3ee234a01d83e15256 hero"
        >
          <div className="hrt-global-wrapper">
            <div className="hero-inner content-width-xlg">
              <div className="title-wrapper">
                <h1 className="title">HeartLink Terms of Service</h1>
              </div>
              <div className="body-text large-concise-body-text modify-text">
                <p>
                  These Terms of Service apply to anyone other than nonprofits
                  who use HeartLink—whether on the HeartLink website or app, or
                  on a nonprofit’s website powered by HeartLink Pro (formerly
                  known as Classy). This includes donors, organizers, and
                  beneficiaries.
                </p>
                <p>
                  Nonprofits that use HeartLink or HeartLink Pro agree to the
                  separate{" "}
                  <Link onClick={toTop} to="/terms">
                    HeartLink Pro Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="block block-long-form-content-with-sidebar block_c32778d03a24a456cc4ccec18c54fa13"
          id="section-1"
        >
          <div className="container-outer" ref={containerRef}>
            <div className="content-wrapper">
              <div className="main-content">
                <p className="effective-date">
                  Effective Date: May 28, 2026
                </p>
                <p>
                  <b>—</b>
                </p>
                <h2 id="h2-number-1">1. Introduction</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>1.1. Purpose of the Terms</strong>: Welcome to
                  HeartLink! These Terms of Service, which we may update from
                  time to time, apply to the HeartLink Platform, including our
                  website at{" "}
                  <Link onClick={toTop} to="/">
                    www.heart-link.it.com
                  </Link>
                  , mobile apps, and any new features we might add. These Terms
                  of Service are important because they help define our
                  relationship with you as you use the Platform. “HeartLink,”
                  “we,” “us,” “our,” and other similar terms, shall refer to the
                  party you are contracting with.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>1.2. Acceptance of Terms</strong>: By using any of the
                  Services or the Platform, you’re agreeing to these Terms of
                  Service. This means you understand and accept all the rules
                  about using our Services. Whether you are contributing to a
                  Fundraiser as a Donor, starting a Fundraiser, withdrawing
                  funds as an Organizer or Beneficiary, or otherwise interacting
                  with the Services, these terms apply to you. Depending on
                  where you live, you might be entering into this agreement with
                  different HeartLink entities.&nbsp; If you are located in the
                  United States, you are contracting with HeartLink, Inc. If you
                  are located in Australia, you are contracting with HeartLink
                  Australia PTY Ltd. in Australia.
                  <span style={{ fontWeight: 400 }}>
                    &nbsp;If you are located in the United Kingdom or Northern
                    Ireland, you are contracting with HeartLink International
                    Services UK Ltd.{" "}
                  </span>
                  If you are located anywhere else in the world, you are
                  contracting with HeartLink Ireland Limited.&nbsp; However,
                  HeartLink, Inc. may apply, and exercise our rights under,
                  these Terms of Service on behalf of the party that you are
                  contracting with.&nbsp; Please see the bottom of these Terms
                  of Service for the addresses and contact information for each
                  of these HeartLink entities.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>1.3. Quick Note on Arbitration</strong>: If you’re
                  using HeartLink in the United States, Australia or Canada, you
                  need to know about our arbitration agreement. By using our
                  Services, you are agreeing that if there’s a disagreement or
                  legal issue between you and HeartLink, it will be resolved
                  through binding arbitration, not in court.&nbsp; You also
                  waive your right to be part of a class action lawsuit or to
                  have a jury trial. Please make sure to read the “Dispute
                  Resolution &amp; Arbitration” section of these Terms of
                  Service for more details.
                </p>
                <h2 id="h2-number-2">2. Definitions</h2>
                <p>
                  These are the key terms that you need to know that will be
                  referenced throughout these Terms of Service:
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.1. Account</strong>: A unique account created by a
                  user to access and utilize HeartLink’s Services, which
                  includes personal information, credentials, and activity logs.
                  This does not include Supporter Accounts created on NPO
                  Websites powered by HeartLink Pro.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.2. Affiliates</strong>: Any entity that directly or
                  indirectly controls, is controlled by, or is under common
                  control with HeartLink. This includes subsidiaries, parent
                  companies, and any other related entities.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.3. AI Service Providers</strong>: Third parties that
                  deploy, provide or support artificial intelligence tools,
                  systems, models, or infrastructure used by HeartLink to enable
                  AI-related features or functionality in connection with the
                  Services, including but not limited to tools used to generate
                  text, images, videos or other User Content.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.4. Beneficiary</strong>: The individual, group or
                  entity for whose benefit the corresponding Fundraiser is
                  conducted and who is the intended recipient of the funds
                  raised.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.5. Certified NPO</strong>: Any non-profit
                  organization that has successfully enrolled with the PayPal
                  Giving Fund to receive donations.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.6. Donor</strong>: Any individual or entity that
                  contributes funds to a Fundraiser on or through the Services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.7. Fundraiser</strong>: A campaign initiated on the
                  Services with the goal of raising funds for a specific purpose
                  for a Beneficiary.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.8. HeartLink Pro</strong>: A suite of fundraising
                  tools, services, and technology solutions provided by
                  HeartLink and its Affiliates to NPOs. This includes both (a)
                  the HeartLink Pro Platform (e.g., the websites, applications,
                  and cloud-based platform, and the associated data,
                  information, tools, and functionalities, that enable NPO’s
                  online fundraising capabilities, whether through direct
                  donations, auctions, streaming or other technologies) and (b)
                  related HeartLink Pro services (e.g., implementation, site
                  planning, configuration, integration, or deployment of the
                  HeartLink Pro Platform, project management, and other
                  consulting or support services). HeartLink Pro is provided
                  under separate agreements between HeartLink and the applicable
                  NPO or business entity.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.9. Messages</strong>: Communications between users
                  sent through the Messaging features on the platform, which may
                  include text, links, images or other media. Messages are
                  distinct from User content and generally viewable only by the
                  sender and intended recipients, but may be monitored,
                  reviewed, or removed by HeartLink as part of our content
                  moderation and safety controls.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.10. NPO</strong>: A non-profit organization
                  (including churches, universities or other educational
                  institutions) that is established for charitable purposes
                  under applicable laws and is eligible to receive donations
                  through the Platform.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.11. NPO Website</strong>:&nbsp; A website, donation
                  page, or other online property created or operated by a
                  third-party NPO that uses HeartLink Pro and through which
                  Users may access, register for, or make donations to the NPO.
                  While an NPO Website may be branded with the NPO’s logo or
                  appear on the NPO’s own domain, the portions of the NPO
                  Website that are powered by HeartLink’s technology (including
                  any donation pages) are governed by these Terms of Service.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.12. Organizer</strong>: An individual or entity
                  (including a non-profit organization) who initiates and
                  manages a fundraiser on the Platform.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.13. Platform</strong>: The entire suite of online
                  Services provided by HeartLink and its Affiliates, including
                  our website, mobile applications, and any related software,
                  services or technologies, including HeartLink Pro.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.14. Profile</strong>: A feature that allows Users to
                  share their activity on HeartLink with other Users, including
                  Fundraisers they have created or donated to (unless donated
                  anonymously), to learn about activity on HeartLink or
                  spotlight specific causes or organizations they care about
                  most, and to follow and engage with other Users.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.15. Recurring Fee</strong>: A recurring fixed fee
                  that we may charge when donors set up a recurring donation to
                  a Fundraiser.&nbsp; To learn more about the Platform and the
                  applicable Recurring Fee, visit{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Pricing
                  </Link>
                  .
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.16. Services</strong>: All features,
                  functionalities, and tools provided by HeartLink and its
                  Affiliates through its Platform, enabling Users to create and
                  manage Fundraisers, Profiles, donate, and interact with each
                  other, including HeartLink Pro.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.17. Services Content</strong>: All content and
                  materials provided by HeartLink on the Services, including
                  text, graphics, logos, images, and software, including
                  HeartLink Pro, but excluding User Content.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.18. Software</strong>: The proprietary software and
                  applications, including cookies and similar technologies,
                  provided and owned by HeartLink that enable users to access
                  and use the Services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.19. Supporter Account</strong>: An account created
                  by a User on an NPO Website powered by HeartLink Pro, which
                  may allow the User to donate, register for events, track
                  donations, or manage fundraising or donation activity.
                  Supporter Accounts are distinct from HeartLink Platform
                  Accounts.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.20. Third Party Resources</strong>: Any websites,
                  Third Party Services, content, software, applications, cookies
                  and similar technologies, or other products or resources
                  provided by entities other than HeartLink or its Affiliates,
                  which may be accessible through the Services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.21. Transaction Fee</strong>: A fee charged by
                  HeartLink or its payment processors for processing donations.
                  To learn more about the Platform and the applicable
                  Transaction Fee, visit{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Pricing
                  </Link>
                  .
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.22. Transfers</strong>: The process of moving funds
                  raised through the Services at the direction of the
                  Beneficiary to either (i) the Beneficiary’s bank account or to
                  (ii) another account designated by the Beneficiary, which may
                  be one of our partners for the delivery of a gift card to the
                  Beneficiary for all or part of the funds raised.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.23. User</strong>: Any individual or entity that
                  accesses, registers for, or uses the Platform, including
                  Organizers, Donors, and Beneficiaries.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.24. User Content</strong>: Any content, activity, or
                  information, including your name, text, images, videos, and
                  other materials, that a User posts, uploads, submits, or
                  otherwise makes available or shares with other Users on or
                  through the Services including in connection with any
                  Fundraiser or Profile, regardless of whether such content is
                  generated using artificial intelligence.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>2.25. User Conduct</strong>: The behaviors and actions
                  of Users while using or otherwise accessing the Services.
                </p>
                <h2 id="h2-number-3">3. The Services We Provide</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>3.1. Description of the Services provided</strong>: We
                  offer a Platform for individuals, entities, or nonprofits to
                  create Fundraisers to collect monetary donations from Donors
                  for themselves, a third party Beneficiary, or an NPO. We also
                  provide a Profiles feature that allows users to share
                  information about their fundraising activities, highlight or
                  follow causes they support.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>3.2. Our Role and Limitations</strong>: Our Services
                  are a tool for running Fundraisers and for helping Organizers
                  connect with Donors; we are not a bank, payment processor,
                  broker, charity, or financial advisor. We do not solicit
                  donations, the existence of the Services is not a solicitation
                  of donations, and we do not engage in solicitation activities
                  for ourselves or others on our Platform.&nbsp; We do not act
                  in, or intend to act in, any capacity that requires
                  registration, licensure, or compliance as a Professional
                  Fundraiser, Commercial Fundraiser, or Professional Fundraising
                  Counsel in any US state, as such terms are defined under
                  applicable laws. All information provided through our Services
                  is for your general knowledge and isn’t meant to be
                  professional advice.&nbsp; If you need specific advice,
                  especially regarding financial, legal, or tax issues, you
                  should consult with a professional. We do not control or
                  endorse any User, Fundraiser, or cause, and we can’t guarantee
                  the success of a Fundraiser.&nbsp; As a Donor, it’s up to you
                  to decide if a cause is worth contributing to.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    3.3. Modification, Suspension, or Termination of the
                    Services
                  </strong>
                  : We can change, pause, or stop all or some of the Services at
                  any time and for any reason. We will try to avoid any problems
                  this might cause you or others, but sometimes we might not be
                  able to give you advance notice, especially if it’s an
                  emergency or required by law. We are not responsible for and
                  expressly disclaim all liability for any issues or alleged
                  damages that these changes might cause. Additionally, to
                  enhance and personalize your experience, we may conduct tests,
                  including A/B testing, which involve displaying different
                  versions of our Services to our users to evaluate and improve
                  our Services and our performance.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>3.4 Recommended Fundraisers and Profiles</strong>
                  :&nbsp; When you search or browse on HeartLink, results may be
                  ranked based on factors such as your location, contacts
                  you&nbsp; may have shared with us, the fundraiser’s category
                  or a cause if we infer it interests you, proximity to its
                  fundraising goal, and how recently it has received donations.
                  These parameters help us personalize your experience and make
                  it easier to find fundraisers you may care about. You can use
                  filters to influence how fundraisers are shown to you.
                </p>
                <h2 id="h2-number-4">
                  4. Account Creation and Eligibility Requirements
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>4.1. What You Need for Registration</strong>: When you
                  sign up to use certain Services, you must provide information
                  that is correct and complete about yourself. This includes
                  your name as it appears on your government-issued
                  identification, address, phone number, occupation, date of
                  birth, and any photos or videos that you may provide, if
                  you’re organizing a Fundraiser or adding information to your
                  Profile. It’s important to always keep this information up to
                  date to ensure everything runs smoothly. Our Privacy Notice
                  and these Terms of Service govern the registration data and
                  any other information you provide. HeartLink offers two types
                  of user accounts depending on where you access our
                  Services:&nbsp; (1) Accounts used to access and interact with
                  the HeartLink Platform; and (2) Supporter Accounts, which may
                  be created when donating to or interacting with an NPO through
                  an NPO Website powered by HeartLink Pro.&nbsp; Each type of
                  account is subject to these Terms of Service, but may offer
                  different functionality and access.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>4.2. Compliance with Third-Party Services</strong>:
                  You might also need to register with third-party payment
                  processors we work with, which may involve agreeing to their
                  terms (provided under the Payment Processor section below).
                  If, at any point, we or our payment processors discover that
                  the information you provided to us is inaccurate, violates our
                  Terms of Service or terms of service of our payment
                  processors, or if you misuse the funds, we may immediately
                  suspend or terminate your access to all or some of our
                  Services. You could also face criminal charges by relevant
                  governmental authorities.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>4.3. Age Restrictions</strong>: If you are under 18,
                  you are prohibited from using our Platform or Services. By
                  using our Platform or our Services, you represent and warrant
                  that you are over the age of 18.&nbsp; Notwithstanding the
                  foregoing, Minors under 18 may be Beneficiaries of Fundraisers
                  on our Platform, subject to compliance with Section 9.4 below.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>4.4. Keeping Your Account Safe</strong>:&nbsp; You are
                  responsible for keeping your password and account information
                  confidential. Do not share your password with anyone. If you
                  think someone else has used your Account or Supporter Account
                  without your permission, you should tell us right away.&nbsp;
                  Also, always remember to log out of your Account or Supporter
                  Account when you’re done using it, especially if you’re on a
                  computer that others can use too.&nbsp; If you don’t keep your
                  Account and/or Supporter Account safe and comply with these
                  Terms of Service, HeartLink won’t be responsible for any
                  losses that you may incur. For more information on how to keep
                  your Account and/or Supporter Account safe, visit our{" "}
                  <Link onClick={toTop} to="/">
                    Account security
                  </Link>{" "}
                  page.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>4.5. Mobile Services and Text Messages</strong>: The
                  Services include certain features that may be made available
                  via a mobile device, including the ability to: (i) upload User
                  Content to the Platform; (ii) browse the Platform; and (iii)
                  access certain items through an application downloaded and
                  installed on a mobile device (collectively, the “Mobile
                  Services”). To the extent you access Mobile Services, your
                  wireless service carrier’s standard charges, data rates and
                  other fees may apply. In addition, downloading, installing, or
                  using certain Mobile Services may be prohibited or restricted
                  by your carrier, and not all Mobile Services may work with all
                  carriers or devices. By using the Mobile Services, you agree
                  that we may communicate with you about matters related to your
                  Account or security by SMS, MMS, text message or other
                  electronic means to your mobile device and that certain
                  information about your usage of the Mobile Services may be
                  communicated to us.&nbsp; Further, when setting up your
                  HeartLink Account, if you click “Send code”, you agree to
                  receive automated and non automated text messages related to
                  your Account from or on behalf of HeartLink at the phone
                  number provided.&nbsp; You can reply STOP to such text
                  messages to cancel, except for automated text messages related
                  to the security of your Account.&nbsp; Message frequency will
                  vary. Message and normal data rates may apply.&nbsp; We will
                  comply with any additional requirements that may apply under
                  local laws and regulations before communicating with you in
                  this manner. In the event that you change or deactivate your
                  mobile telephone number, you agree to promptly update your
                  HeartLink Account information to ensure that your messages are
                  not sent to the person that acquires your old number.
                  Additionally, you agree to the{" "}
                  <Link onClick={toTop} to="/terms">
                    SMS Terms of Service
                  </Link>{" "}
                  as it applies to the provision, receipt, and delivery of text
                  messages by HeartLink.
                </p>
                <h2 id="h2-number-5">5. Payment Processors</h2>
                <p style={{ paddingLeft: 40 }}>
                  HeartLink itself does not hold any funds raised on our
                  Platform, nor does it handle the actual processing of
                  payments. Instead, we use third-party payment processors to
                  manage and process all donations for Fundraisers.&nbsp; To
                  Transfer funds from a Fundraiser, you must provide your bank
                  account details to our payment processor partners.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  By making a Donation, setting up a Fundraiser or accepting the
                  role of Beneficiary to a Fundraiser, you agree to the
                  processing, use, Transfer or disclosure of data by the Payment
                  Processors pursuant to these Terms of Service as well as any
                  and all applicable terms set forth by the applicable Payment
                  Processors.&nbsp; Our current Payment Processors include:
                  Adyen LLC (
                  <Link
                    onClick={toTop}
                    to="https://www.adyen.com/legal/terms-and-conditions-adyen-for-platforms/"
                  >
                    Adyen’s terms and conditions
                  </Link>
                  ), Stripe, Inc. (
                  <Link onClick={toTop} to="https://stripe.com/us/legal">
                    Stripe’s terms of service
                  </Link>
                  ), and PayPal, Inc. (
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/ga/webapps/mpp/ua/legalhub-full"
                  >
                    PayPal’s terms of service
                  </Link>
                  ), and{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/us/webapps/mpp/givingfund/policies/donor-terms-of-service"
                  >
                    PayPal Giving Fund terms
                  </Link>
                  . Our current payment processor for Organizers and
                  Beneficiaries located in the EEA, Switzerland and the UK is
                  Adyen N.V. which contracts with HeartLink Ireland, Ltd.
                  <span style={{ fontWeight: 400 }}>
                    , and HeartLink International Services UK Ltd.
                  </span>{" "}
                  Please see Adyen N.V.’s terms and conditions{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.adyen.com/legal/terms-and-conditions-adyen-for-platforms/"
                  >
                    here
                  </Link>
                  . HeartLink’s payment partner in Australia is Adyen Australia.
                  Adyen Australia contracts with HeartLink’s Australian entity,
                  HeartLink Australia Pty Ltd (ACN 627 702 630), is the
                  contracting party responsible for transactions benefitting the
                  Australian User. For more information, please see{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.adyen.com/legal/combined-financial-services-guide"
                  >
                    Combined Financial Services Guide and Product Disclosure
                    Statement
                  </Link>
                  .
                </p>
                <p style={{ paddingLeft: 40 }}>
                  For more details, including how we handle data with these
                  third party payment processors, please refer to our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>
                  , Adyen LLC (
                  <Link
                    onClick={toTop}
                    to="https://www.adyen.com/legal/terms-and-conditions-adyen-for-platforms/"
                  >
                    Adyen’s terms and conditions
                  </Link>
                  ), Stripe, Inc. (
                  <Link onClick={toTop} to="https://stripe.com/us/legal">
                    Stripe’s terms of service
                  </Link>
                  ), and PayPal, Inc. (
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/ga/webapps/mpp/ua/legalhub-full"
                  >
                    PayPal’s terms of service
                  </Link>
                  ).
                </p>
                <h2 id="h2-number-6">
                  6. User Responsibilities and Obligations
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>6.1. Organizers</strong>:&nbsp; As an Organizer, you
                  represent and warrant that all User Content you provide about
                  your Fundraiser is accurate, complete, and clear. &nbsp;This
                  applies whether the User Content is provided directly by you,
                  through an agent, or generated using an AI service provider or
                  any other artificial intelligence tool. You are responsible
                  for describing on your Fundraiser how funds will be used, and
                  ensuring the funds raised are only used for that specific
                  purpose.&nbsp; You may post updates to your Fundraiser so
                  Donors know how their money is being used, and any other
                  relevant information.&nbsp; If you are raising funds on behalf
                  of someone else, you must ensure that all funds raised are
                  given to or spent on behalf of that Beneficiary. By adding a
                  Beneficiary, you relinquish control of the donations for your
                  Fundraiser to the Beneficiary. When you organize a Fundraiser,
                  you agree to follow all laws and regulations related to your
                  Fundraiser, including but not limited to those about taxes and
                  donations. If you are using personal data from anyone,
                  including but not limited to their name, image or
                  likeness,&nbsp; you must have their valid legal permission to
                  share it with us and post it on the Services.&nbsp; You also
                  agree not to provide or offer to provide goods or services in
                  exchange for donations.&nbsp; We may share information about
                  your Fundraiser with Donors, the Beneficiary, legal
                  authorities, and as otherwise described in our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>
                  .
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)&nbsp; Maintaining Accurate Information</strong>:
                  Keeping your registration details accurate and current is
                  essential. This includes updating your name, address, and any
                  images or videos you use to represent yourself or your
                  organization. This helps maintain transparency and trust with
                  Donors and ensures compliance with these Terms of Service and
                  applicable legal requirements.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(b)&nbsp; You Agree to Cooperate</strong>: When you
                  organize a Fundraiser on HeartLink, you agree to fully
                  cooperate with any request for evidence that we deem necessary
                  to verify your compliance with these Terms of Service.&nbsp;
                  Our requests may include, but are not limited to, asking that
                  you: (a) explain how funds were or will be handled; (b) supply
                  documentary evidence of the circumstances described on your
                  Fundraiser; (c) share the identity of any party receiving,
                  benefitting from, or involved with handling all or any portion
                  of the funds; (d) supply proof of how funds were or will be
                  used; or&nbsp; (e) supply evidence that the intended third
                  party Beneficiary consents to a plan for distribution of funds
                  consistent with the description of your Fundraiser.&nbsp; We
                  reserve the right to refuse, condition, suspend, freeze or ban
                  any Donations, Accounts, Supporter Accounts, Fundraisers,
                  Transfers or other transactions that we believe in our sole
                  discretion may violate these Terms of Service or harm the
                  interests of our Users, business partners, the public, or
                  HeartLink, or that expose you, HeartLink, or others to risks
                  unacceptable to us.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (c) Fundraisers on an NPO Website Powered by HeartLink Pro
                  </strong>
                  : If you create a Fundraiser on an NPO Website powered by
                  HeartLink Pro, we may provide you with access to the contact
                  information of Donors who contribute to your Fundraiser
                  (unless they choose to donate anonymously).&nbsp; You may use
                  this Donor information solely to contact those Donors about
                  your fundraising activity – including future fundraising
                  activity – in support of the same NPO. You may not sell,
                  share, or otherwise use this donor information for any
                  unrelated or commercial purpose. You are solely responsible
                  for complying with all applicable laws when contacting Donors,
                  including any requirements to include opt-out instructions or
                  honor unsubscribe requests under laws like CAN-SPAM, GDPR, or
                  similar laws in the Donor’s location.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>6.2. Donors</strong>:&nbsp; When you donate money on
                  HeartLink, it’s your responsibility to understand how your
                  donation will be used, and you donate at your own risk. Make
                  sure to regularly check the Fundraiser’s page for any updates
                  or new information. We are not responsible for what Organizers
                  promise or offer in their Fundraisers. We are also not
                  responsible for verifying information that appears on
                  Fundraisers, nor do we guarantee that donations will be used
                  in accordance with any fundraising purpose prescribed by a
                  User or Fundraiser.&nbsp; However, we take all reports of
                  fraud or misuse of funds very seriously, and we will take
                  appropriate action against any Fundraiser or User that
                  violates our Terms of Service. We also protect Donors through
                  the{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Giving Guarantee
                  </Link>
                  . Unless you choose to donate anonymously, you consent to
                  HeartLink using or sharing your name or contact information to
                  promote donation matching, suggest fundraising goals, or to
                  otherwise encourage others to donate, provided however,
                  HeartLink will obtain additional consents, if required under
                  applicable laws.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)&nbsp; Making Donations</strong>: When you donate
                  to a Fundraiser or NPO through HeartLink or an NPO Website
                  powered by HeartLink Pro, you need to use a credit card or
                  other payment method that is linked to your Account or your
                  Supporter Account, as applicable. You represent and warrant
                  that your payment information is correct and that you are
                  legally authorized to use your payment method. There may be a
                  minimum amount you can donate, and once you donate your money
                  will not be refunded unless HeartLink decides to give a refund
                  pursuant to Section 7.1(b) or pursuant to the{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Giving Guarantee
                  </Link>
                  . When you donate, we will save your preferred payment method
                  so we can make it easier for you to donate in the future. We
                  use third party payment processors to process your donation.
                  As explained further in Section 5 “Payment Processors”, by
                  donating, you also agree to allow our payment processors to
                  handle your payment information according to their rules and
                  these Terms.&nbsp; For details on the companies we use for
                  processing payments and their rules, please check the “Payment
                  Processors” section above.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(b)&nbsp; Recurring Donations</strong>:&nbsp; By
                  opting into recurring donations, you authorize HeartLink and
                  its vendors to charge your designated payment method without
                  further authorization from you. You agree that the donation
                  amount you specify will be charged to your payment method at
                  the frequency you select (e.g., monthly) until the earlier of
                  (1) the Fundraiser ceasing to be a valid, active Fundraiser or
                  (2) as soon as reasonably practical, after you elect to cancel
                  this recurring donation. You may cancel your recurring
                  donation at any time by contacting our Support Team or through
                  the cancellation feature in your Account or Supporter Account
                  as part of Services. Cancellation will take effect promptly
                  upon receipt of your cancellation request, and no further
                  donations will be processed after the cancellation date.
                  However, any donations processed prior to your cancellation
                  will not be refunded unless due to other reasons such as the
                  fundraiser not being valid.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(c)&nbsp; Reporting Concerns</strong>:&nbsp; We take
                  any reports of fraud or misuse of funds very seriously.&nbsp;
                  You can learn more about how we handle fraud and misuse by
                  visiting our page on{" "}
                  <Link onClick={toTop} to="/">
                    How We Protect Our Community
                  </Link>
                  . If you have reason to believe that a User or Fundraiser is
                  not raising or using the funds for their stated purpose,
                  please use the “Report” button that appears at the bottom of
                  every Fundraiser to alert our team of this potential issue and
                  we will investigate.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (d)&nbsp; No Restrictions When You Donate to NPOs
                  </strong>
                  : If you donate to an NPO, either on HeartLink or through an
                  NPO Website, you can’t decide exactly how your donation is
                  used. Even if you donate for a specific project or tell the
                  NPO how you’d like your donation used, these instructions are
                  just suggestions. The NPO has the final say and can use all
                  donations at their discretion.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(e) Tax Implications of Donating to NPOs</strong>
                  :&nbsp; When you donate to an NPO, you should talk to a tax
                  advisor to understand if your donation is tax-deductible.
                  HeartLink doesn’t withhold funds for tax purposes or
                  otherwise, nor does HeartLink guarantee that your donations
                  are tax-deductible or eligible for tax credits. HeartLink will
                  also not be liable for any claims or penalties assessed by any
                  tax authorities regarding how your donation is reported by you
                  or a third party.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (f)&nbsp; We share your Information with NPOs you Donate to
                  </strong>
                  :&nbsp; In certain cases, we may share your personal
                  information (even if you’ve chosen to donate anonymously),
                  with the NPO to which you donate, in accordance with our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>
                  . The NPO may use this information only to communicate with
                  you about their legal compliance and fundraising purposes.
                  HeartLink isn’t responsible for how the NPO uses your
                  information.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(g) ACH Donations</strong>:&nbsp; When you donate to a
                  Fundraiser or NPO through HeartLink via automated clearing
                  house (“ACH”) transaction, you authorize HeartLink to
                  electronically debit the bank account you linked to your
                  Account or Supporter Account (and, if necessary,
                  electronically credit your account to correct any erroneous
                  debits).&nbsp; You agree that ACH transactions you authorize
                  comply with all applicable law.&nbsp; You understand that this
                  authorization will remain in full force and effect until you
                  notify HeartLink Support via our{" "}
                  <Link onClick={toTop} to="/">
                    Help Center
                  </Link>{" "}
                  that you wish to revoke this authorization.&nbsp; You
                  understand that HeartLink requires at least two days prior
                  notice in order to cancel this authorization.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (h) Donations Made on NPO Websites Powered by HeartLink Pro
                  </strong>
                  : HeartLink provides certain fundraising technology to NPOs
                  through a suite of tools and services known as HeartLink Pro.
                  When you access, use, register for, or make a donation through
                  an NPO Website, your use of the NPO Website and donation
                  activity are governed by these Terms of Service, just as if
                  you were using HeartLink’s own website or app. These Terms
                  apply regardless of whether the NPO Website is branded with
                  the nonprofit’s logo or hosted on its own domain.&nbsp; For
                  the avoidance of doubt all NPO clients that use HeartLink Pro
                  are subject to their own, separate agreements with us that
                  contain terms relating to our provision of services to them.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  If you donate to a Fundraiser created by an individual
                  Organizer on an NPO website, your name and email address may
                  also be shared with that Organizer.&nbsp; The Organizer may
                  use this information only to contact you about fundraising
                  activity in support of that same NPO.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(i) Payment Processors for NPOs</strong>: HeartLink
                  has partnered with PayPal and, in limited circumstances, Adyen
                  or Stripe, to process Donations to NPOs. Although exceptions
                  can be made, NPOs in the United States, United Kingdom,
                  Ireland, Canada and Australia will be defaulted to using the
                  PayPal Giving Fund to process Donations made through the
                  Services. The manner in which transactions are processed is
                  explained below. NPOs in other countries will be defaulted to
                  using Adyen or Stripe. Donations made to NPOs using HeartLink
                  Pay to process transactions will be defaulted to Stripe or
                  Paypal.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(j) PayPal Giving Fund</strong>:&nbsp; In the United
                  States, Donations to support Certified NPOs will be made to
                  PayPal Giving Fund, a grant-giving IRS-registered 501(c)(3)
                  public charity. PayPal Giving Fund receives Donations from
                  Users as the NPO of record and then, in accordance with its
                  policies, grants Funds to the NPO selected by the Organizer
                  and identified in the Fundraiser. While PayPal Giving Fund
                  takes multiple steps to attempt to grant donated funds
                  according to the Donor’s identified preference, PayPal Giving
                  Fund retains exclusive control over all Donations. If a
                  Certified NPO fails to meet the terms of{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/us/webapps/mpp/givingfund/policies/nonprofit-certification-policy"
                  >
                    PayPal Giving Fund’s Nonprofit Certification Policy
                  </Link>{" "}
                  and/or fails to meet PayPal Giving Fund’s due diligence
                  standards for receipt of a grant of donated funds at any time,
                  PayPal Giving Fund may reassign the funds in accordance with
                  its{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/us/webapps/mpp/givingfund/policies/donation-delivery-policy"
                  >
                    Donation Delivery Policy
                  </Link>
                  . When PayPal Giving Fund receives your Donation, it will
                  grant the Donation to the NPO of your choice, but PayPal
                  Giving Fund will provide your tax receipt. Donations made to
                  PayPal Giving Fund are governed by PayPal Giving Fund’s{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/us/webapps/mpp/givingfund/policies/privacy-policy"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    onClick={toTop}
                    to="https://www.paypal.com/us/webapps/mpp/givingfund/policies/donor-terms-of-service"
                  >
                    Donor Terms of Service
                  </Link>
                  .&nbsp; PayPal Giving Fund’s entities outside the United
                  States are also registered NPOs in their countries. For
                  example, if an Organizer in the United Kingdom starts a
                  Fundraiser for an NPO in the United Kingdom, then PayPal will
                  act as the Payment Processor with PayPal Giving Fund UK
                  receiving the Donations.&nbsp; Thereafter, PayPal Giving Fund
                  UK will grant funds to the NPO identified in the Fundraiser in
                  accordance with its policies, and it will issue a tax receipt
                  for all Donations received through it.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    6.3. Tax Responsibilities for Organizers and Beneficiaries
                  </strong>
                  : We do not withhold funds for tax purposes or otherwise. You,
                  as an Organizer or Beneficiary are solely responsible for
                  paying any applicable taxes in connection with any donations
                  you receive.&nbsp; It’s up to you to calculate, report, and
                  pay the correct amount of tax to the tax authorities.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>6.4. Profiles</strong>:&nbsp; When using your Profile
                  you agree to adhere to all the terms regarding User Content
                  and User Conduct set forth in Section 8 (“Prohibited
                  Fundraisers and Related User Content”) and Section 9
                  (“Prohibited User Conduct”) below.&nbsp; This means, among
                  other things, that you may not use your Profile to post
                  misleading, false, or unlawful content. Notwithstanding the
                  foregoing, you may use a username or other identifier on your
                  Profile or your custom public profile URL, but we reserve the
                  right to remove it if we believe it is inappropriate (such as
                  when a trademark owner complains about a username or
                  identifier that does not closely relate to your actual name or
                  when it otherwise violates these Terms of Service). HeartLink
                  reserves the right, at its sole discretion and without prior
                  notice, to suspend your Profile and to remove or modify any
                  User Content included on your Profile that violates these
                  Terms of Service.&nbsp; By choosing to make your Profile
                  public, you acknowledge that your Profile information,
                  activities and related User Content may be visible to other
                  Users and searchable on the HeartLink Platform. This means
                  that your username and/or name, profile photo, bio,
                  fundraising and donation activity, who you follow and your
                  followers, and any other activity you engage in on the
                  Platform may be visible to others.&nbsp; By making your
                  Profile public, you give us permission to display information
                  associated with your Profile—such as your username, profile
                  photo, and interactions (e.g., likes, follows)—in connection
                  with other content or accounts, including fundraisers, public
                  posts, and in content or other features that appear on or
                  through the Platform. When your profile is set to private,
                  only limited information such as your name, photo, bio and
                  follower and following counts will remain visible to other
                  Users, but the rest of your activities on the HeartLink
                  Platform will not be publicly displayed.&nbsp; You can control
                  your Account settings to make your Profile either public or
                  private. For more information on how your data will be
                  collected and used, please see our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>
                  .
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>6.5. Messaging</strong>: You may not use Messages to
                  harass, solicit off-platform payments, send spam or scams, or
                  share content or engage in any conduct that otherwise violates
                  these Terms of Service. Your Messages may be subject to
                  moderation and review (including through automated tools such
                  as AI) to protect the safety of the Platform and its Users,
                  and to improve our Services. HeartLink reserves the right to
                  limit access to Messaging, remove Messages, or take other
                  enforcement actions in accordance with these Terms.
                </p>
                <h2 id="h2-number-7">
                  7. Transfers, Holds, Returns &amp; Chargebacks
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>7.1. Transfers</strong>: While we strive to make
                  Transfers available to you promptly, our ability to do so is
                  dependent upon Users providing the right information and upon
                  our technical systems operating as intended.&nbsp; You
                  acknowledge and agree that: (i) Transfers may not be available
                  to you for use immediately; (ii) we do not guarantee that
                  Transfers will be always available to you within any specific
                  time frame, but we will use commercially reasonable efforts to
                  provide Transfers as soon as reasonably possible; (iii) you
                  will cooperate with any request we make for evidence we deem
                  necessary to verify your compliance with these Terms of
                  Service; and (iv) to the extent permitted by applicable law,
                  we expressly disclaim any and all responsibility for any delay
                  in Transfers or your inability to access and use donated funds
                  at any specified time, and any consequences arising from such
                  delay or inability.&nbsp; We will use commercially reasonable
                  efforts to let you know when you can expect to receive the
                  Transfer and to provide a clear timeframe whenever possible.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)&nbsp; You must provide accurate info</strong>:
                  You, as an Organizer and/or Beneficiary, are responsible for
                  (i) verifying your personal information and bank account
                  information under “Set up transfers” as soon as possible; and
                  (ii) ensuring that the information you provide to HeartLink
                  and/or its Affiliates in order to process a Transfer,
                  including bank account information, is accurate and up to
                  date.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(b)&nbsp; Refunds</strong>: We may, in our sole
                  discretion, offer or issue a refund of Donation(s), which may
                  comprise the entire amount donated to your Fundraiser. Refunds
                  may be issued with or without prior notice, depending on the
                  circumstances.&nbsp; For example, we may refund donors based
                  on fraud prevention, violation of our Terms of Service, card
                  scheme compliance, failure to provide requested evidence of
                  compliance with our Terms of Service, legal obligations, or
                  other reasons. To the extent permitted by applicable law, we
                  will not be liable to you or any third party for any claims,
                  damages, costs, losses, or other consequences caused by
                  refunds, including, but not limited to, transaction or
                  overdraft fees. Subject to our HeartLink Giving Guarantee, we
                  will determine the appropriate resolution in cases where
                  Donors, Organizers, or Beneficiaries request a refund.&nbsp;
                  Where a Beneficiary does not want the funds (or the portion of
                  funds that exceed the Fundraiser goal) and an Organizer or
                  Beneficiary requests a refund, or where a Beneficiary fails to
                  complete the necessary steps to withdraw funds within 120 days
                  of the first donation (unless otherwise extended at
                  HeartLink’s discretion), HeartLink may, in its sole
                  discretion, offer refunds to donors or redirect funds to a
                  verified NPO whose mission aligns with the stated purpose of
                  the Fundraiser where appropriate and permitted by law. Where
                  practical, we will notify donors and provide an opportunity
                  for them to request a refund before redirecting funds to such
                  NPO.&nbsp; Nothing in this section shall modify or limit the
                  terms of the HeartLink Giving Guarantee in any way.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(c)&nbsp; Transfer Funds within 120 Days</strong>: Our
                  payment processors are not able to hold funds
                  indefinitely.&nbsp; If you do not Transfer your donations to
                  your bank account within one hundred and twenty (120) days of
                  your first donation, our payment processors may, in accordance
                  with relevant laws and regulations, refund or escheat any
                  funds raised. You will be responsible for working with the
                  appropriate governmental authority to claim any escheated
                  funds.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>7.2. Transfer Holds</strong>: We may, in our sole
                  discretion, place a hold on a Fundraiser, restrict Transfers,
                  initiate a reverse ACH Transfer, secure reserves, or take
                  similar actions to protect our Users (any of these actions may
                  be referred to as a “Hold”).&nbsp; We may put a Hold on your
                  Fundraiser or Account for several reasons, including but not
                  limited to:
                </p>
                <p style={{ paddingLeft: 80 }}>
                  (a)&nbsp; We need more information to verify that your
                  Fundraiser complies with our Terms of Service, or we have
                  determined that a Fundraiser or User has violated our Terms of
                  Service;
                </p>
                <p style={{ paddingLeft: 80 }}>
                  (b)&nbsp; Our determination that the funds should be provided
                  directly to a person other than the Organizer, such as a legal
                  Beneficiary or person entitled by law to act on behalf of an
                  Organizer;
                </p>
                <p style={{ paddingLeft: 80 }}>
                  (c)&nbsp; We have determined, in our sole discretion, that the
                  use of funds has materially changed from the original purpose
                  of the Fundraiser, necessitating refunds to Donors;
                </p>
                <p style={{ paddingLeft: 80 }}>
                  (d)&nbsp; Such action is required to comply with a court
                  order, injunction, writ, or as otherwise required under
                  applicable law and regulations.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  If you have questions about a Hold we may have placed on your
                  Fundraiser or Account, or need information about how to
                  resolve the Hold, please see this article and check the email
                  account used to register for additional information.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>7.3. Payment Processor Chargebacks or Returns</strong>
                  : Occasionally, a Donor may dispute a credit card or ACH
                  charge for a Donation through the Services or submit a request
                  for a refund under the{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Giving Guarantee
                  </Link>
                  .&nbsp; If you believe that an error has occurred in any of
                  your donations, please contact us immediately via our{" "}
                  <Link onClick={toTop} to="/">
                    Help Center
                  </Link>{" "}
                  so we can help resolve the issue. Any fraud disputes or
                  chargebacks initiated with your payment provider may be
                  contested by us on the basis of this authorization.&nbsp; In
                  situations where the Cardholder is not alleging that a
                  fraudulent donation has been made (i.e. that the transaction
                  was not made by the cardholder), the Cardholder should always
                  attempt to resolve the dispute with the Organizer or with us
                  before pursuing a chargeback or return.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  We will review each request to determine whether the request
                  is legitimate, and if we determine a request is not
                  legitimate, we may use information submitted by you and/or at
                  our disposal at the time of donation to defend that request.
                  Further, we will defend any non-fraud request with such reason
                  codes as Merchandise / Services Not Received or any other code
                  that suggests non-delivery, as transactions processed on
                  HeartLink are donations without the expectation or exchange of
                  goods or services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  If a Donor disputes their transaction with their bank or card
                  issuer, or if the card issuer disputes the transaction on
                  behalf of the cardholder and it becomes a chargeback or
                  return, the Donor surrenders, without limitation, any benefits
                  or protections of the HeartLink Giving Guarantee related to
                  that Donation.
                </p>
                <h2 id="h2-number-8">
                  8. Prohibited Fundraisers and Related User Content
                </h2>
                <p>
                  This Section includes our rules about prohibited and/or
                  illegal Fundraisers and User Content. We may remove any User
                  Content or Messages–including any Fundraisers–that we
                  determine violates these Terms of Service, regardless of
                  whether such User engaged in such conduct by leveraging
                  artificial intelligence (“AI”) provided by HeartLink, our AI
                  Service Providers or otherwise.&nbsp; Further, if you violate
                  these Terms of Service, we may ban or disable your use of the
                  Services, stop payments to any Fundraiser, freeze or place a
                  hold on donations and Transfers, report you to law enforcement
                  authorities, or take any other appropriate legal action.
                </p>
                <p>
                  We may investigate a Fundraiser, a User or User Content at any
                  time to ensure compliance with these Terms of Service. In
                  doing so, we may consider all available material, including
                  but not limited to social media, related news, and any other
                  information that we deem relevant in our review.&nbsp; Please
                  note that while we reserve the right to remove, edit, or
                  modify any content on our Platform at our sole discretion, we
                  are not obligated to do so. This includes content that is
                  illegal, inaccurate, misleading, infringes on intellectual
                  property rights, or violates these Terms of Service.
                </p>
                <p>
                  You agree that you will not use the Services or Platform to
                  raise funds, establish any Fundraiser, or post any User
                  Content for the purposes of promoting or involving:
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.1.</strong> the violation of any law, regulation,
                  industry requirement, or third-party guidelines or agreements
                  by which you are bound, including those of payment card
                  providers that are utilized in connection with the Services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.2.</strong> Fundraisers that are fraudulent,
                  misleading, inaccurate, dishonest or impossible;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.3.</strong> offensive, graphic, perverse or sexual
                  content;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.4.</strong> the funding of a ransom, human
                  trafficking or exploitation, vigilantism, bribes or bounty;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.5.</strong> purchase or use by an organization or
                  individual of drugs, narcotics, steroids, controlled
                  substances, pharmaceuticals or similar products or therapies
                  that are either illegal, or prohibited at the state or
                  national level;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.6.</strong> activities with, in, or involving
                  countries, regions, governments, persons, or entities that are
                  subject to U.S. and other economic sanctions under applicable
                  law, unless such activities are expressly authorized by the
                  appropriate governmental authority and by our payment service
                  providers;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.7.</strong> any equipment or weapons meant for use
                  in conflict or by an armed group,&nbsp; explosives,
                  ammunition, firearms, knives, or other weaponry or
                  accessories;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.8.</strong> any activity in support of terrorism,
                  extremism, hate, violence, harassment, bullying,
                  discrimination, terrorist financing, extremist financing, or
                  money laundering;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.9.</strong> User Content that reflects, incites or
                  promotes bullying, harassment, discrimination, or intolerance
                  of any kind relating to race, ethnicity, national origin,
                  religious affiliation, sexual orientation, sex, gender, gender
                  identity, gender expression, disabilities or diseases;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.10.</strong> the legal defense of financial and
                  violent crimes, including those related to money laundering,
                  murder, robbery, assault, battery, sex crimes or crimes
                  against minors;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.11.</strong> User Content that promotes self-harm or
                  suicide except as permitted by law in a clinical setting;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.12.</strong> gambling, gaming and/or any other
                  activity with an entry fee and a prize including, but not
                  limited to raffles, casino games, sports betting, fantasy
                  sports, horse or greyhound racing, lottery tickets, raffle
                  tickets, auctions and other ventures that facilitate gambling,
                  games of skill or chance (whether or not it is legally defined
                  as a lottery), promotions involving rewards (monetary or
                  otherwise) in exchange for donations, including event tickets,
                  raffle entries, meet-and-greet opportunities, gift cards or
                  sweepstakes;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.13.</strong> any activity that disguises, conceals,
                  or otherwise obscures the origin of funds;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.14.</strong> annuities, investments with the
                  expectation of a return, loans, equity or lottery contracts,
                  lay-away system, off-shore banking or similar transactions,
                  money service businesses (including currency exchanges, check
                  cashing or the like), pyramid schemes, “get rich quick
                  schemes” (i.e., investment opportunities or other services,
                  that promise high rewards), network marketing and referral
                  marketing programs, debt collection or crypto-currencies;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.15.</strong> the receipt or grant of cash advances
                  or lines of credit to yourself or to another person for any
                  reason, including but not limited to self payments, or
                  payments for which there is no apparent purpose;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.16.</strong> products or services that directly
                  infringe or facilitate infringement upon the trademark,
                  patent, copyright, trade secrets, or proprietary or privacy
                  rights of any third party, including but not limited to
                  counterfeit music, movies, software, or other licensed
                  materials without the appropriate authorization from the
                  rights holder;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.17.</strong> the promotion, advertisement, sale or
                  resale of goods or services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.18.</strong> electoral fundraising unless the
                  Fundraiser is directly managed by the candidate or their
                  committee; any election Fundraiser in an unsupported country
                  unless run by a registered organization within a supported
                  country;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.19.</strong> any attempt to bypass or otherwise
                  circumvent payment processing rules and regulations, or these
                  Terms of Service;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.20.</strong> any activity that presents HeartLink
                  with an unacceptable risk of financial loss;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>8.21.</strong> any other activity that HeartLink may
                  deem, in its sole discretion, to: (a) be unacceptable or
                  objectionable; (b) restrict or inhibit any other person from
                  using or enjoying the Services; or (c) expose HeartLink, its
                  employees or Users to any harm or liability of any type.
                </p>
                <p>
                  Please note that if you are located in the United Kingdom, you
                  will be permitted to offer give-away incentives (e.g., one
                  sticker, while supplies last, for every donation made) in
                  connection with Your Fundraiser. &nbsp; Otherwise, You are not
                  permitted to offer any contest, competition, reward,
                  give-away, raffle, sweepstakes or similar activity (each, a
                  “Promotion”) on or through the Services.
                </p>
                <h2 id="h2-number-9">9. Prohibited User Conduct</h2>
                <p>
                  This Section includes our rules about User Conduct that is
                  prohibited and/or illegal. We may remove any User Content or
                  Messages–including any Fundraisers–if we determine that a
                  relevant User has engaged in User Conduct that violates these
                  Terms of Service, regardless of whether such User engaged in
                  such conduct by leveraging AI provided by HeartLink, our AI
                  Service Providers or otherwise.&nbsp; Further, if you violate
                  these Terms of Service, we may ban or disable your use of the
                  Services, stop payments to any Fundraiser, freeze or place a
                  hold on donations and Transfers, report you to law enforcement
                  authorities, or otherwise take appropriate legal action.
                </p>
                <p>By using the Services or our Platform, you agree:</p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.1.</strong> not to use the Services to transmit or
                  otherwise upload any User Content that: (i) infringes any
                  intellectual property or other proprietary rights of any
                  party; (ii) you do not have a right to upload under any law or
                  under contractual or fiduciary relationships; (iii) contains
                  software viruses or any other computer code, files or programs
                  designed to interrupt, destroy or limit the functionality of
                  any computer software or hardware or telecommunications
                  equipment; (iv) poses or creates a privacy or security risk to
                  any person; or (v) constitutes unsolicited or unauthorized
                  advertising, promotional materials, commercial activities
                  and/or sales, “junk mail,” “spam,” “chain letters,” “pyramid
                  schemes,” “contests,” “sweepstakes,” or any other form of
                  solicitation;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.2.</strong> not to interfere with or disrupt servers
                  or networks connected to or used to provide the Services or
                  their respective features, or disobey any requirements,
                  procedures, policies or regulations of the networks connected
                  to or used to provide the Services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.3.</strong> not to harvest, collect, scrape or
                  publish personally identifiable information of others;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.4.</strong> not to raise funds for a minor unless
                  (i) you have obtained express permission of the minor’s
                  guardian; or (ii) the funds are Transferred into a trust, UTMA
                  or UGMA account for the sole benefit of the minor;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.5.</strong> not to use the Services on behalf of a
                  third party or post any personal data or other information
                  about a third party, without the express consent of that third
                  party;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.6.</strong> not to use another User’s Account,
                  Supporter Account, Profile, or URL without permission,
                  impersonate any person or entity, falsely state or otherwise
                  misrepresent your affiliation with a person or entity,
                  misrepresent an NPO or Fundraiser through the Services, or
                  post User Content in any inappropriate category or areas on
                  the Services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.7.</strong> not to create any liability for
                  HeartLink or cause us to lose (in whole or in part) the
                  services of our Internet Service Provider(s), web hosting
                  company or any other vendors or suppliers;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.8.</strong> not to gain unauthorized access to the
                  Services, or any account, computer system, or network
                  connected to the Services, by any unauthorized or illegal
                  means;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.9.</strong> not to obtain or attempt to obtain any
                  materials or information not intentionally made available
                  through the Services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.10.</strong> not to use the Services to post,
                  transmit or in any way exploit any information, software or
                  other material for commercial purposes, or that contain
                  advertising, except that using the Services for fundraising
                  activities in accordance with these Terms of Service is
                  expressly permitted;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.11.</strong> not to transmit more request messages
                  through the Services in a given period of time than a human
                  can reasonably produce in the same period by using a
                  conventional online web browser;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.12.</strong> not to undertake any activity or engage
                  in any conduct that is inconsistent with the business or
                  purpose of the Services;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.13.</strong> not to share your password or login
                  credentials with anyone for any reason;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.14.</strong> not to make or accept any Donations
                  that you know or suspect to be erroneous, suspicious or
                  fraudulent;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.15.</strong> not to use the Services in or for the
                  benefit of a country, organization, entity, or person
                  embargoed or blocked by any government, including those on
                  sanctions lists identified by the United States Office of
                  Foreign Asset Control (OFAC) nor as applicable in your
                  country;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.16.</strong> not to use bots, automated scripts,
                  software, or any other method not expressly authorized by
                  HeartLink to artificially generate follows, followers, or any
                  other form of inauthentic engagement.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.17.</strong> not to attempt to undertake indirectly
                  any of the foregoing;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.18.</strong> to maintain reasonable and standard
                  security measures to protect any information transmitted and
                  received through the Services, including without limitation,
                  adhering to any security procedures and controls required by
                  HeartLink from time to time;
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.19.</strong> to maintain a copy of all electronic
                  and other records related to Fundraisers and Donations as
                  necessary for HeartLink to verify compliance with these Terms
                  of Service and make such records available to HeartLink upon
                  our request. For clarity, the foregoing does not affect or
                  limit your obligations to maintain documentation as required
                  by applicable laws, rules, regulations, or governmental
                  authority; and
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.20.</strong> at HeartLink’s request, to cooperate as
                  far as reasonably possible and permitted under applicable law,
                  in the auditing of, investigation of (including without
                  limitation, investigations by HeartLink, a Payment Processor,
                  or a regulatory or governmental authority), and remedial
                  efforts to correct any alleged or uncovered violation or
                  wrongdoing of a User to whom, or Fundraiser or Donation to
                  which, you are connected.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>9.21.</strong> &nbsp;Not to convert funds raised
                  through the Platform into cryptocurrency for the purposes of
                  delivering those funds to its intended recipients, including
                  Beneficiaries. All funds must be withdrawn and delivered in
                  fiat currency.
                </p>
                <h2 id="h2-number-10">
                  10. Content Moderation and Reporting Fundraisers
                </h2>
                <p>
                  If you have reason to believe that a Fundraiser contains
                  illegal content or content that violates these Terms of
                  Service or our content moderation policies, please use the
                  “Report” button on the Fundraiser to alert our team of this
                  potential issue and we will investigate.&nbsp; If you disagree
                  with a decision taken by us in connection to a notice or
                  complaint related to User Content and/or use of the Platform
                  that is illegal or prohibited by HeartLink, you may use our
                  available dispute resolution systems.
                </p>
                <p>
                  We leverage a combination of business rules, machine learning,
                  and human review to identify and remediate violations of our
                  Terms of Service. Additional information related to how we
                  moderate User Content, how we protect individuals from illegal
                  content, the process for reporting or appealing violations,
                  the process for handling and resolution of complaints and the
                  dispute resolution systems available can be found in our{" "}
                  <Link onClick={toTop} to="/">
                    Help Center
                  </Link>
                  . You acknowledge that rules in this section concerning
                  content moderation are without prejudice to
                  policies/procedures available in our Help Center regulating
                  how we moderate User Content, how we protect individuals from
                  illegal content, the process for reporting or appealing
                  violations, the process for handling and resolution of
                  complaints and the dispute resolution systems available.
                </p>
                <h2 id="h2-number-11">11. Fees</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>11.1. We Don’t Charge a Platform Fee:</strong>&nbsp; A
                  Platform fee is an upfront, calculated charge, either fixed or
                  percentage-based, for accessing or using a specific service on
                  a Platform. We do not charge a Platform fee, and we do not
                  charge to start or maintain a Fundraiser.&nbsp; However, a
                  Transaction Fee does apply to donations received and, if
                  donors choose recurring donations, we charge donors a
                  Recurring Fee on each donation.&nbsp; Notwithstanding the
                  foregoing, if you are using our suite of online fundraising
                  tools for non-profits to power donations on your own website,
                  separate fees may apply.&nbsp; The pricing and fees terms in
                  your existing contract with us will remain in effect for any
                  payments facilitated by us on your entity’s website until the
                  contract terminates in accordance with its terms. Such pricing
                  will not be modified by claiming your NPO Profile or agreeing
                  to HeartLink’s Terms of Service.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    11.2. A Transaction Fee Is Charged on All Donations and A
                    Recurring Fee is Charged for Recurring Donations
                  </strong>
                  :&nbsp; A Transaction Fee is the cost of processing a
                  payment.&nbsp; Although there are no Platform fees to start or
                  maintain a Fundraiser, please keep in mind that a Transaction
                  fee, including credit and debit charges, is deducted from each
                  donation by our Payment Processors to securely deliver your
                  donations. If donors choose recurring donations, we charge
                  donors a Recurring Fee per donation to the selected
                  Fundraiser. To learn more about our fees, please visit{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Pricing
                  </Link>
                  .
                </p>
                <h2 id="h2-number-12">
                  12. Intellectual Property Rights, Content Ownership and
                  Licenses
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>12.1. Ownership and intellectual property</strong>
                  :&nbsp; You acknowledge that the Services Content are
                  protected under laws related to copyright, patent, trademark,
                  and other proprietary rights.&nbsp; The technology and
                  Software that support the Services, or are distributed in
                  connection with them, are owned by HeartLink, our Affiliates,
                  and our partners.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>12.2. Use of HeartLink Trademarks</strong>: The
                  HeartLink name and logos are trademarks of HeartLink and its
                  Affiliates (collectively the “HeartLink Trademarks”). Other
                  company, product, and service names and logos displayed on our
                  Services or Platform may be trademarks of their respective
                  owners, who may or may not be affiliated with us.&nbsp;
                  Nothing in these Terms of Service or our Services gives you
                  permission to use any HeartLink Trademarks without our prior
                  written consent. All goodwill generated from the use of
                  HeartLink Trademarks benefits us exclusively.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    12.3. Do Not Misappropriate Content on our Site
                  </strong>
                  : You agree not to alter, copy, frame, scrape, rent, lease,
                  loan, sell, distribute, or create derivative works based on
                  the Services, the Services Content or User Content. You agree
                  not to use any data mining, spiders, robots, scraping, or
                  similar data gathering or extraction methods to extract or
                  copy any of the Services Content or User Content in any form
                  or otherwise in connection with your use of the
                  Services.&nbsp; You are expressly prohibited from using,
                  scraping, crawling, or otherwise collecting any content (User
                  Content or otherwise) from HeartLink, for the purpose of
                  training, developing, or operating any machine learning
                  models, large language models, or any other artificial
                  intelligence systems, including foundation or frontier models,
                  without the express prior written consent of HeartLink. Any
                  unauthorized use of HeartLink content, including for AI model
                  training, constitutes a violation of these terms and may
                  result in the suspension or termination of your account and
                  legal action.&nbsp; If we block your access to the Services
                  (including blocking your IP address), you agree not to bypass
                  this blocking (for example, by masking your IP address or
                  using a proxy IP address). Any rights not expressly granted
                  herein by us are reserved.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>12.4. Do Not Misappropriate our Software</strong>: You
                  are prohibited from copying, modifying, creating derivative
                  works of, reverse engineering, disassembling, or attempting to
                  discover any source code of the Software or the Services in
                  any form.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    12.5. Rights and Permissions for User Content You Share
                  </strong>
                  : When you share content through our Services, including
                  through your Profile or when interacting with AI, you
                  represent and warrant that you either own the content or have
                  permission to use and share it. This includes all related
                  copyrights, trademarks, and rights to privacy or
                  publicity.&nbsp; When you upload, share, or make any User
                  Content available in connection with the Services, you grant
                  HeartLink and its Affiliates the worldwide, royalty free,
                  transferable, sublicensable, perpetual, irrevocable license to
                  copy, display, distribute, store, modify, translate, publish,
                  prepare derivative works, or otherwise use that User Content
                  for any purpose, including for the promotion, advertisement or
                  marketing of our Services in any form of media.&nbsp; This
                  license includes the right for us and our AI Service Providers
                  to analyze and process the User Content to enhance and develop
                  the Services and related technologies, including through the
                  use of machine learning and AI models (whether generative,
                  predictive, or another type).&nbsp; This license enables us,
                  for example, to use User Content to train and refine models
                  that support safety features, fraud prevention, content
                  moderation, customer support, and product optimization. We do
                  not use your content to train AI models for the purpose of
                  developing general-purpose commercial AI products unrelated to
                  the Services. You further acknowledge that your participation
                  in the Services and submission of User Content is voluntary
                  and that you will not receive financial compensation of any
                  type associated with the licenses, waivers, and releases set
                  forth herein (or HeartLink’s exploitation thereof), and that
                  the sole consideration for subject matter of this agreement is
                  the opportunity to use the Services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    12.6. Release and Waiver of Rights in User Content
                  </strong>
                  : When you post any User Content and to the maximum extent
                  permitted by law, you irrevocably waive any moral rights in
                  your User Content against us and our Users, and agree to
                  release and hold harmless HeartLink, our contractors and our
                  employees from (i) any claims for invasion of privacy,
                  publicity or libel; (ii) any liability from the use of your
                  name, image, or likeness, including blurring, distortion,
                  alteration or other uses; and (iii) any liability for claims
                  made by you related to your User Content, name, image or
                  likeness. By posting User Content, you also waive any right to
                  inspect or approve any intermediary or finished versions of
                  your User Content. If your User Content includes anyone other
                  than yourself, you represent and warrant that you have
                  obtained all necessary permissions, waivers, and releases from
                  those individuals. This ensures that HeartLink can use the
                  content as described above without any legal issues.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>12.7. User Submissions and Feedback</strong>: Any
                  content or information you provide to HeartLink, whether
                  solicited or not, may be publicly accessible. This includes
                  any information you post on forums, in comment sections,
                  surveys, customer support communications, or any other
                  submissions such ideas, suggestions or feedback about the
                  Services (“Submissions and Feedback”). By submitting any
                  Submissions or Feedback, you agree that (i) we have no
                  obligation to keep Submissions or Feedback confidential; (ii)
                  we may already have similar information under consideration or
                  development; (iii) we can use and distribute Submissions and
                  Feedback for any purpose, without acknowledgement or
                  compensation to you; (iv) you have all necessary rights to
                  submit such Submissions and Feedback; (v) you grant HeartLink
                  a&nbsp; perpetual, worldwide, royalty-free, irrevocable,
                  non-exclusive, and fully transferable license to use,
                  reproduce, perform, display, distribute, adapt, modify,
                  re-format, create derivative works of, and otherwise exploit
                  other information, including the right to sublicense these
                  rights; and (vi) You waive any moral rights or equivalent
                  claims to the extent permitted by law.&nbsp; This section
                  remains effective even after your Account or use of the
                  Services is terminated.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>12.8. Copyright or Trademark Complaints</strong>: We
                  respect the intellectual property of others, and we ask that
                  our Users do the same. We will process and investigate notices
                  of alleged copyright or trademark infringement and take
                  appropriate action under the Digital Millennium Copyright Act
                  (“DMCA”) or other locally equivalent intellectual property
                  laws.&nbsp; In our sole discretion, we may terminate the
                  Accounts of any Users who infringe on others’ intellectual
                  property rights.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)&nbsp; Takedown Notice</strong>:&nbsp; If you
                  believe that your work appears on our site in a way that
                  constitutes copyright infringement, or that your intellectual
                  property rights have otherwise been violated, you must notify
                  us in writing, as follows:
                </p>
                <p style={{ paddingLeft: 80 }}>
                  Email our copyright agent at GFMLegal@heart-link.it.com with
                  the subject line (“DMCA Takedown Request”), or mail your
                  request to:
                </p>
                <p style={{ paddingLeft: 120 }}>HeartLink Copyright Agent:</p>
                <p style={{ paddingLeft: 120 }}>
                  HeartLink
                  <br />
                  c/o Legal Department
                  <br />
                  PO Box 121270
                  <br />
                  815 E Street
                  <br />
                  San Diego, CA 92101
                  <br />
                  United States
                </p>
                <p style={{ paddingLeft: 40 }}>
                  To be effective, your Takedown Notice must contain the
                  following information:
                </p>
                <ul>
                  <li style={{ listStyleType: "none" }}>
                    <ul>
                      <li style={{ listStyleType: "none" }}>
                        <ul>
                          <li style={{ listStyleType: "none" }}>
                            <ul>
                              <li>
                                Your complete contact information (full name,
                                mailing address and phone number).&nbsp; Please
                                note that we may provide your contact
                                information to the person who posted the content
                                you are reporting.&nbsp; For this reason, you
                                may wish to provide a professional or business
                                email address;
                              </li>
                              <li>
                                a description of the copyrighted work that you
                                claim has been infringed;
                              </li>
                              <li>
                                A description of the contention on our site that
                                you claim infringes your copyright;
                              </li>
                              <li>
                                a description of where the material that you
                                claim is infringing is located on the Services.
                                The easiest way to do this is by providing web
                                addresses (URLs) leading directly to the
                                allegedly infringing content.
                              </li>
                              <li>
                                A declaration that:
                                <ul>
                                  <li>
                                    You have a good faith belief that the
                                    disputed use is not authorized by the
                                    copyright owner, its agent, or the law;
                                  </li>
                                  <li>
                                    The information in your notice is accurate;
                                    and
                                  </li>
                                  <li>
                                    Under penalty of perjury, you are the owner
                                    or authorized to act on behalf of the owner
                                    of an exclusive copyright that is allegedly
                                    infringed.
                                  </li>
                                </ul>
                              </li>
                              <li>Your electronic or physical signature.</li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(b) Counter-Notice</strong>: If you believe that your
                  User Content that was either removed or disabled is
                  non-infringing or that you have authorization from the owner,
                  the owner’s agent, or pursuant to the law, to upload and use
                  such content, you may send us a counter-notice to our
                  copyright agent at GFMLegal@heart-link.it.com or the address
                  listed above.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  To be effective, your counter-notice must include the
                  following information:
                </p>
                <ul>
                  <li style={{ listStyleType: "none" }}>
                    <ul>
                      <li style={{ listStyleType: "none" }}>
                        <ul>
                          <li style={{ listStyleType: "none" }}>
                            <ul>
                              <li>
                                Your complete contact information (full name,
                                mailing address and phone number).&nbsp; Please
                                note that we may provide your contact
                                information to the person who complained about
                                your content.&nbsp; For this reason, you may
                                wish to provide a professional or business email
                                address;
                              </li>
                              <li>
                                identification of the content that has been
                                removed or to which access has been disabled and
                                the location at which the content appeared
                                before it was removed or disabled;
                              </li>
                              <li>
                                A declaration that:
                                <ul>
                                  <li>
                                    You have a good-faith belief that the
                                    content was removed or disabled as a result
                                    of mistake or a misidentification of the
                                    content;
                                  </li>
                                  <li>
                                    You consent to the jurisdiction of the
                                    Federal District Court for the judicial
                                    district in which your address is
                                    located.&nbsp; If you are outside the United
                                    States, you must consent to the jurisdiction
                                    of any Federal District court in any
                                    judicial district in which HeartLink
                                    operates; and
                                  </li>
                                  <li>
                                    You will accept service of process from or
                                    on behalf of the person who provided the
                                    DMCA takedown notice, or an agent of such
                                    person.
                                  </li>
                                </ul>
                              </li>
                              <li>Your physical or electronic signature.</li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </li>
                </ul>
                <p>
                  If we receive a counter-notice, we will send a copy of the
                  counter-notice to the original complaining party, informing
                  that person that we may replace the removed content or cease
                  disabling it in 10 business days. Unless the original
                  complaining party files an action seeking a court order
                  against the content provider or User, the removed content may
                  be replaced, or access to it restored, in 10 to 14 business
                  days or more after receipt of the counter-notice.
                </p>
                <h2 id="h2-number-13">13. Data Privacy</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>13.1. Privacy Notice</strong>: At HeartLink, we
                  respect the privacy of our Users. For details, please see our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>
                  .&nbsp; By using the Services, you acknowledge our collection,
                  use and sharing of personal data as outlined therein.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>13.2. Retention of Fundraiser Data</strong>: We are
                  not obligated to retain data related to any Account, Supporter
                  Account, or Fundraiser after its conclusion.&nbsp; We may
                  delete historical data or terminate inactive Accounts or
                  Supporter Accounts without notice, except for any data we must
                  keep to comply with legal or regulatory compliance obligations
                  or to establish, exercise or defend legal claims.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    13.3. Understanding Public Visibility and Privacy Settings
                  </strong>
                  : Some things you do on our Platform are public, like the
                  posts you make and the material you upload (this could be
                  anything from descriptions, photos, videos, comments, to music
                  and logos). Also, the Profile information you provide (like
                  your name, organization, photo, and biography) can be seen by
                  other Users to help people connect within the Service.&nbsp;
                  For example, if you’re organizing a Fundraiser, you might
                  share personal details, such as a hospital stay, which are
                  sensitive. If you’re donating, you can choose to show your
                  donation publicly, meaning anyone on the internet, including
                  search engines like Google, can see it. If you want to keep
                  your donation private, just check the “Don’t display my name
                  publicly on the fundraiser” box when you donate. Keep in mind,
                  the Fundraiser Organizer, their team, the Beneficiary and
                  others will still see your information according to our
                  Privacy Notice.&nbsp; Messages are not public, but may be
                  accessed, reviewed, or disclosed by HeartLink in accordance
                  with our{" "}
                  <Link onClick={toTop} to="/privacy">
                    Privacy Notice
                  </Link>{" "}
                  and these Terms, including for content moderation, safety, and
                  legal compliance purposes.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>13.4. Third-Party Communications</strong>: When using
                  our Services to communicate with third parties (e.g.,
                  discussing a Fundraiser or Donation), you confirm you have the
                  authority and necessary consents from the third party to share
                  their data with us and that you have informed them about how
                  their information will be collected and used by
                  HeartLink.&nbsp; You also agree that we may use such data to
                  contact the third party or provide you with a template or
                  prepopulated message to facilitate communication and that we
                  may send reminders or related messages to you and the third
                  party.&nbsp; Without limiting the foregoing, in the event you
                  are sending any text message to any resident of the State of
                  Washington, you agree that (i) you will not send, or cause to
                  be sent, any prepopulated message provided by us, (ii) you
                  will obtain clear and affirmative consent from the person
                  before sending them them a text, and (iii) if the recipient
                  indicates that they do not wish to receive additional text
                  messages, you will ensure you do not send any additional text
                  messages to them.
                </p>
                <h2 id="h2-number-14">14. Artificial Intelligence</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>Artificial Intelligence</strong>: We’re constantly
                  developing new technologies and features to improve our
                  Services. For example, we may enable you to use AI features
                  developed by us and/or our AI Service Providers to make it
                  easier for you to write, create assets or other content and
                  promote your use of our Services such as helping you draft
                  fundraisers, posts, create photos or videos to share your
                  fundraisers, or for otherwise optimizing the use of our
                  Services. AI features may also include a conversational
                  chatbot or AI agent that will assist Users by guiding them
                  through the fundraiser creation and management process. Use of
                  these features is offered solely as a convenience to you, and
                  such features are offered on an as-is basis with no warranties
                  of any kind.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a) AI Chat Recordings</strong>:&nbsp; If you use our
                  AI-powered chat agent, we will disclose when you are
                  interacting with AI rather than a human.&nbsp; Additionally,
                  your chats may be recorded and/or monitored by us and our AI
                  Service Providers for training, content moderation and quality
                  assurance purposes.&nbsp; By using these AI features, you
                  consent to such recordings.&nbsp; Notwithstanding the
                  foregoing, HeartLink is not obligated to review or retain your
                  interactions with AI on our Platform.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (b) AI Accuracy, Limitations and User Responsibility
                  </strong>
                  : AI-generated content is probabilistic, meaning it may not
                  always be unique across users and may contain errors,
                  inaccuracies, or offensive content that does not reflect
                  HeartLink’s views.&nbsp; AI-generated content should not be
                  relied upon for legal, financial, medical, or any other
                  professional advice.&nbsp; You are solely responsible for any
                  content you provide to any AI agent on HeartLink, as well as
                  for reviewing, editing, and ensuring the accuracy and
                  appropriateness of all AI-generated content before
                  incorporating it into your Fundraiser or otherwise sharing
                  such content.&nbsp; Exercise discretion before using
                  AI-generated content on the Platform, on social media or in
                  any public or fundraising communications.&nbsp; Like all
                  content you share on our Platform, you are responsible for
                  ensuring any content you generate using an AI agent complies
                  with these Terms of Service, including not sharing misleading
                  information or other User Content that infringes on the rights
                  of others.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (c) Political Fundraising and AI-Generated Content
                  </strong>
                  : Users who utilize AI-generated content for political
                  fundraising-related communications are responsible for
                  complying with all applicable laws, including any requirements
                  to disclose AI-generated media in connection with political
                  fundraising-related communications.&nbsp; Users may not use
                  AI-generated content in a manner that is intended to mislead
                  Users or voters, impersonate candidates, or otherwise deceive
                  the public in violation of any applicable laws.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(d) AI Content Moderation</strong>: You acknowledge
                  that any information you provide and any information generated
                  while using our AI features will be shared with our AI Service
                  Providers to enable your use of our AI features and for
                  content moderation and other purposes consistent with our
                  Terms of Service. You may not use the AI Products in a manner
                  that violates any terms or policy of any AI Service Provider.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(e) Other Restrictions on AI Use</strong>:
                  AI-generated content may not be used to develop machine
                  learning models or related technology. Our optional AI
                  features may not be construed as acting as an agent for
                  HeartLink, and may not bind us to any obligations, legal or
                  otherwise. We do not guarantee the availability of the AI
                  features in any or all geographical areas, and we reserve the
                  right to modify, limit, or discontinue access to AI features
                  on its platform at any time.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  You may not use the Services, including any AI features
                  available on the Platform, to develop machine learning models
                  or related technology.
                </p>
                <h2 id="h2-number-15">15. Third-Party Services and Content</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    15.1.&nbsp; Other Third Party Websites/Links/Services
                  </strong>
                  . Our Services may rely on, or certain third parties may
                  include in our Services links to, certain Third Party
                  Resources. We have&nbsp; no control over such Third Party
                  Resources and do not endorse or otherwise take any
                  responsibility for such Third Party Resources. By using our
                  Services, you agree that we are not liable for the content,
                  functions, accuracy, or legality of these Third Party
                  Resources, or any damages or loss that may be caused by these
                  Third Party Resources.&nbsp; In certain situations, Third
                  Party Resources may include products or services offered by a
                  third party that you may display or are otherwise made
                  available through the Services and if so, you may be subject
                  to third-party terms associated with such Third Party
                  Resources. HeartLink has relationships with certain providers
                  of such products and services, and we may be paid by such
                  providers in the form of commissions in connection with these
                  products and services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>15.2. Apple-Enabled Software Applications</strong>:
                  HeartLink offers Software applications that are intended to be
                  operated in connection with products made commercially
                  available by Apple Inc. (“Apple”), among other platforms. With
                  respect to Software that is made available for your use in
                  connection with an Apple-branded product (such software,
                  “Apple-Enabled Software”), in addition to the other terms and
                  conditions set forth in these Terms of Service, the following
                  terms and conditions apply:
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)</strong>&nbsp; HeartLink and you acknowledge that
                  these Terms of Service are concluded between HeartLink and you
                  only, and not with Apple, and that as between HeartLink and
                  Apple, HeartLink, not Apple, is solely responsible for the
                  Apple-Enabled Software and the content thereof (except for
                  User Content).
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(b)</strong>&nbsp; You may not use the Apple-Enabled
                  Software in any manner that is in violation of or inconsistent
                  with the Usage Rules set forth for Apple-Enabled Software in,
                  or otherwise be in conflict with, the App Store Terms of
                  Service.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(c)</strong> Your license to use the Apple-Enabled
                  Software is limited to a non-transferable license to use the
                  Apple-Enabled Software on an iOS Product that you own or
                  control, as permitted by the Usage Rules set forth in the App
                  Store Terms of Service.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(d)</strong>&nbsp; Apple has no obligation whatsoever
                  to provide any maintenance or support services with respect to
                  the Apple-Enabled Software.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(e)</strong>&nbsp; Apple is not responsible for any
                  product warranties, whether express or implied by law. In the
                  event of any failure of the Apple-Enabled Software to conform
                  to any applicable warranty, you may notify Apple, and Apple
                  will refund the purchase price for the Apple-Enabled Software
                  to you, if any; and, to the maximum extent permitted by
                  applicable law, Apple will have no other warranty obligation
                  whatsoever with respect to the Apple-Enabled Software, or any
                  other claims, losses, liabilities, damages, costs or expenses
                  attributable to any failure to conform to any warranty, which
                  will be HeartLink’s sole responsibility, to the extent it
                  cannot be disclaimed under applicable law.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(f)</strong>&nbsp; HeartLink and you acknowledge that
                  HeartLink, not Apple, is responsible for addressing any claims
                  of you or any third party relating to the Apple-Enabled
                  Software or your possession and/or use of that Apple-Enabled
                  Software, including, but not limited to: (i) product liability
                  claims; (ii) any claim that the Apple-Enabled Software fails
                  to conform to any applicable legal or regulatory requirement;
                  and (iii) claims arising under consumer protection or similar
                  legislation.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(g)</strong>&nbsp; In the event of any third party
                  claim that the Apple-Enabled Software or the end-user’s
                  possession and use of that Apple-Enabled Software infringes
                  that third party’s intellectual property rights, as between
                  HeartLink and Apple, HeartLink, not Apple, will be solely
                  responsible for the investigation, defense, settlement and
                  discharge of any such intellectual property infringement
                  claim.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(h)</strong>&nbsp; If you have any questions,
                  complaints or claims with respect to the Apple-Enabled
                  Software, they should be directed to HeartLink as follows:
                </p>
                <p style={{ paddingLeft: 80 }}>
                  HeartLink
                  <br />
                  c/o Legal Department
                  <br />
                  PO Box 121270
                  <br />
                  815 E Street
                  <br />
                  San Diego, CA 92101
                  <br />
                  United States
                </p>
                <p style={{ paddingLeft: 80 }}>Or Legal@heart-link.it.com</p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(i)</strong>&nbsp; HeartLink and you acknowledge and
                  agree that Apple, and Apple’s subsidiaries, are third-party
                  beneficiaries of these Terms of Service with respect to the
                  Apple-Enabled Software, and that, upon your acceptance of the
                  terms and conditions of these Terms of Service, Apple will
                  have the right (and will be deemed to have accepted the right)
                  to enforce these Terms of Service against you with respect to
                  the Apple-Enabled Software as a third-party Beneficiary
                  thereof. Accordingly, the parties acknowledge and agree that
                  HeartLink enters into this clause (“Apple-Enabled Software
                  Applications”) for its own benefit and on its own behalf and
                  also as an agent for the benefit and on behalf of Apple and
                  its subsidiaries with respect to the exercise and enforcement
                  of all rights, benefits and remedies of Apple and its
                  subsidiaries (but not any obligation or burden) in this clause
                  (“Apple-Enabled Software Applications”) which rights, benefits
                  and remedies shall be enforceable by HeartLink in its own
                  right and also as agent for and on behalf of each of Apple and
                  its subsidiaries. HeartLink may amend, terminate or rescind
                  these Terms of Service without the consent of Apple or any
                  such subsidiary.
                </p>
                <h2 id="h2-number-16">
                  16. Suspension or Termination of Accounts
                </h2>
                <p>
                  You agree that we may, in response to concerns of fraudulent
                  or illegal activity or a material breach of these Terms of
                  Service, suspend or terminate your Account (or any part
                  thereof), Supporter Account, or your access to the Services
                  and remove and discard any User Content or data at any time,
                  including any Fundraisers you may have organized.&nbsp; To the
                  extent permitted by applicable law, we may take any such
                  actions without any liability to you or to any third party for
                  any claims, damages, costs or losses resulting
                  therefrom.&nbsp; We may take such actions with or without
                  providing you notice.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>16.1. Account Closures</strong>:&nbsp; We reserve the
                  right, without limitation, to close your Account, Supporter
                  Account, or disable your access to the Services in any of the
                  following circumstances: (i) we are unable to confirm that
                  your Fundraiser complies with these Terms of Service; (ii) we
                  are unable to support your Account or Supporter Account from a
                  technical perspective; (iii) our payment processors are unable
                  to support your Account or Supporter Account; (iv) the
                  Beneficiary requests that the Fundraiser is removed; (v) your
                  Account or Supporter Account becomes dormant or otherwise
                  abandoned; (vi) your Account or Supporter Account displays
                  activity that poses a risk to HeartLink or its community; or
                  (vii) such action(s) is required to comply with a court order,
                  writ, injunction, or as otherwise required under applicable
                  laws and regulations. If we close your Account or Supporter
                  Account or otherwise disable your access to the Services for
                  any of these reasons, we may also issue refunds, as
                  appropriate, in accordance with these Terms of Service and the{" "}
                  <Link onClick={toTop} to="/">
                    HeartLink Giving Guarantee
                  </Link>
                  .
                </p>
                <h2 id="h2-number-17">
                  17. Disclaimers and Limitations of Liability
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>17.1.&nbsp; Warranty Disclaimer</strong>:&nbsp; YOUR
                  USE OF THE SERVICES IS AT YOUR SOLE RISK. TO THE EXTENT
                  PERMITTED BY APPLICABLE LAW, THE SERVICES ARE PROVIDED ON AN
                  “AS IS” AND “AS AVAILABLE” BASIS. EACH OF HeartLink AND ITS
                  AFFILIATES EXPRESSLY DISCLAIM AND EXCLUDE, TO THE FULLEST
                  EXTENT PERMITTED BY APPLICABLE LAW, ALL WARRANTIES, CONDITIONS
                  AND REPRESENTATIONS OF ANY KIND, WHETHER EXPRESS, IMPLIED OR
                  STATUTORY, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
                  WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                  PURPOSE, TITLE AND NON-INFRINGEMENT.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  WITHOUT PREJUDICE TO&nbsp; STATUTORY WARRANTIES UNDER
                  APPLICABLE LAW, AND WITHOUT PREJUDICE TO CONTENT MODERATION
                  REQUIREMENTS WHERE PRESCRIBED BY APPLICABLE LAW, NEITHER
                  HeartLink NOR ANY OF ITS AFFILIATES MAKE ANY WARRANTY OR CLAIM
                  THAT: (I) THE SERVICES WILL MEET YOUR REQUIREMENTS; (II) THE
                  SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE;
                  (III) THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE
                  SERVICES WILL BE ACCURATE OR RELIABLE; OR (IV) THE QUALITY OF
                  ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER MATERIAL
                  PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICES WILL MEET
                  YOUR EXPECTATIONS.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  ALL THIRD-PARTY INFORMATION AND CONTENT ON THE SERVICES ARE
                  FOR INFORMATIONAL PURPOSES ONLY. HeartLink AND ITS AFFILIATES
                  DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, TIMELINESS, OR
                  RELIABILITY OF THIS INFORMATION. NO CONTENT IS INTENDED TO
                  PROVIDE FINANCIAL, LEGAL, TAX, OR OTHER PROFESSIONAL ADVICE.
                  BEFORE MAKING DECISIONS ABOUT FUNDRAISERS, NPOS, DONATIONS, OR
                  ANY RELATED INFORMATION, CONSULT WITH YOUR FINANCIAL, LEGAL,
                  TAX, OR OTHER PROFESSIONAL ADVISOR. YOU ACKNOWLEDGE THAT YOU
                  ACCESS ALL INFORMATION AND CONTENT ON THE SERVICES AT YOUR OWN
                  RISK.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  WE DO NOT GUARANTEE THAT ANY FUNDRAISER WILL RECEIVE A
                  SPECIFIC AMOUNT OF DONATIONS OR ANY DONATIONS AT ALL. WE DO
                  NOT ENDORSE ANY FUNDRAISER, USER, OR CAUSE, AND WE MAKE NO
                  GUARANTEES ABOUT THE ACCURACY OF INFORMATION PROVIDED THROUGH
                  THE SERVICES. AS A DONOR, YOU MUST DETERMINE THE VALUE AND
                  APPROPRIATENESS OF CONTRIBUTING TO ANY USER OR FUNDRAISER.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>17.2. LIMITATION OF LIABILITY</strong>:&nbsp; YOU
                  EXPRESSLY UNDERSTAND AND AGREE THAT, TO THE FULLEST EXTENT
                  PERMITTED BY APPLICABLE LAW, NEITHER HeartLink NOR ANY OF ITS
                  AFFILIATES WILL BE LIABLE FOR ANY: (I) INDIRECT, INCIDENTAL,
                  SPECIAL, CONSEQUENTIAL, PUNITIVE OR EXEMPLARY DAMAGES; (II)
                  DAMAGES FOR LOSS OF PROFITS; (III) DAMAGES FOR LOSS OF
                  GOODWILL; (IV) DAMAGES FOR LOSS OF USE; (V) LOSS OR CORRUPTION
                  OF DATA; OR (VI) OTHER INTANGIBLE LOSSES (EVEN IF HeartLink
                  HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES), WHETHER
                  BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR
                  OTHERWISE, RESULTING FROM: (A) THE USE OR THE INABILITY TO USE
                  THE SERVICES INCLUDING ANY AI FEATURES; (B) THE COST OF
                  PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES RESULTING FROM
                  ANY GOODS, DATA, INFORMATION OR SERVICES PURCHASED OR OBTAINED
                  OR MESSAGES RECEIVED OR TRANSACTIONS ENTERED INTO THROUGH OR
                  FROM THE SERVICES; (C) ANY PROMOTIONS AND RELATED PRIZES OR
                  REWARDS MADE AVAILABLE THROUGH THE SERVICES; (D) UNAUTHORIZED
                  ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR DATA; (E)
                  STATEMENTS OR CONDUCT OF ANY THIRD PARTY ON THE SERVICES; OR
                  (F) ANY OTHER MATTER RELATING TO THE SERVICES. TO THE FULLEST
                  EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL
                  HeartLink’S TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES
                  (INCLUDING CONTRACT, NEGLIGENCE, STATUTORY LIABILITY OR
                  OTHERWISE) OR CAUSES OF ACTION EXCEED THE AMOUNT YOU HAVE PAID
                  HeartLink IN THE LAST SIX (6) MONTHS, OR, IF GREATER, ONE
                  HUNDRED U.S. DOLLARS (US$100).
                </p>
                <p style={{ paddingLeft: 40 }}>
                  YOU AGREE THAT NEITHER HeartLink NOR ANY OF ITS AFFILIATES
                  SHALL BE LIABLE IN ANY WAY FOR ANY CONTENT OR MATERIALS OF ANY
                  THIRD PARTIES (INCLUDING USERS), ANY USER CONTENT (INCLUDING,
                  BUT NOT LIMITED TO, FOR ANY ERRORS OR OMISSIONS IN ANY USER
                  CONTENT), OR FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A
                  RESULT OF THE USE OF ANY SUCH USER CONTENT. YOU AGREE THAT
                  HeartLink IS NOT RESPONSIBLE FOR ANY ACTIONS YOU TAKE BASED ON
                  CONTENT OR OUTPUTS GENERATED THROUGH AI FEATURES, NOR FOR ANY
                  ERRORS, OMISSIONS, OR MISREPRESENTATIONS CONTAINED IN
                  AI-GENERATED CONTENT. USE OF THESE FEATURES IS AT YOUR OWN
                  RISK. YOU ACKNOWLEDGE THAT HeartLink DOES NOT PRE-SCREEN ALL
                  USER CONTENT, BUT THAT HeartLink AND ITS DESIGNEES WILL HAVE
                  THE RIGHT (BUT NOT THE OBLIGATION) IN THEIR SOLE DISCRETION TO
                  REFUSE, REMOVE, OR ALLOW ANY USER CONTENT THAT IS AVAILABLE
                  VIA THE SERVICES AT ANY TIME AND FOR ANY REASON, WITH OR
                  WITHOUT NOTICE, AND WITHOUT ANY LIABILITY TO YOU OR TO ANY
                  THIRD PARTY FOR ANY CLAIMS, DAMAGES, COSTS OR LOSSES RESULTING
                  THEREFROM. WE EXPRESSLY DISCLAIM ANY LIABILITY FOR THE OUTCOME
                  OR SUCCESS OF ANY FUNDRAISER.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN
                  WARRANTIES OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR
                  INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE
                  LIMITATIONS SET FORTH ABOVE MAY NOT APPLY TO YOU. IF YOU ARE
                  DISSATISFIED WITH ANY PORTION OF THE SERVICES OR WITH THESE
                  TERMS OF SERVICE, YOUR SOLE AND EXCLUSIVE REMEDY IS TO
                  DISCONTINUE USE OF THE SERVICES.
                </p>
                <h2 id="h2-number-18">18. Indemnification and Release</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    18.1. Obligations of Users to Indemnify HeartLink against
                    certain types of claims
                  </strong>
                  : To the fullest extent permitted by applicable law, You agree
                  to release, indemnify and hold HeartLink and its Affiliates
                  and their officers, employees, directors and agents harmless
                  from any and all losses, damages, expenses (including
                  reasonable attorneys’ fees), costs, awards, fines, claims, and
                  actions of any kind, arising out of or relating to your use of
                  the Services, any Donation or Fundraiser, any User Content or
                  content generated by AI, your connection to the Services, your
                  violation of these Terms of Service or your violation of any
                  rights of another. You agree that HeartLink has the right to
                  conduct its own defense of any claims at its own discretion,
                  and that you will indemnify HeartLink for the costs of its
                  defense.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>18.2. Release of Claims</strong>: If you are a
                  California resident, you waive California Civil Code Section
                  1542, which says: “A general release does not extend to claims
                  that the creditor or releasing party does not know or suspect
                  to exist in his or her favor at the time of executing the
                  release, and that if known by him or her would have materially
                  affected his or her settlement with the debtor or released
                  party.” If you are a resident of another jurisdiction—in or
                  outside of the United States—you waive any comparable statute
                  or doctrine to the extent permitted by law.
                </p>
                <h2 id="h2-number-19">
                  19. Dispute Resolution &amp; Arbitration
                </h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    19.1. USERS IN THE UNITED STATES AND ANYWHERE ELSE IN THE
                    WORLD EXCEPT FOR THE EUROPEAN ECONOMIC AREA, THE UK AND
                    SWITZERLAND:
                  </strong>
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    PLEASE READ THIS SECTION CAREFULLY&nbsp; BEFORE USING THE
                    SERVICES OR PLATFORM AS THIS AGREEMENT AFFECTS YOUR LEGAL
                    RIGHTS AND GOVERNS HOW CLAIMS THAT YOU AND WE MAY HAVE
                    AGAINST EACH OTHER ARE RESOLVED.
                  </strong>{" "}
                  This Agreement requires you to arbitrate disputes with
                  HeartLink and limits the manner in which you can seek relief.
                  This Agreement limits certain legal rights, including the
                  right to a jury trial, the right to participate in any form of
                  class, collective, or representative claim, dispute or action,
                  and the right to certain remedies and forms of relief.&nbsp;
                  Other rights that we or you would have in court, such as an
                  appellate review, also may not be available in the arbitration
                  process described within this section.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(a)&nbsp; Informal Resolution</strong>: You and
                  HeartLink agree that good-faith informal efforts to resolve
                  disputes can often result in a prompt, low-cost and mutually
                  beneficial outcome. In the unlikely event that a disagreement
                  arises between you and HeartLink regarding any claim or
                  controversy at law or equity arising out of, relating to, or
                  connected in any way with the Services or the Platform
                  (collectively, “Dispute”), prior to initiating any legal
                  action, you must first contact us directly by email at
                  gfmlegal@heart-link.it.com. You must provide your name, the
                  email address associated with your HeartLink account (if any),
                  a description of the Dispute and the specific relief sought.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  You agree that the term “Dispute” in these Terms of Service
                  will have the broadest meaning possible. These Terms also
                  cover any Dispute between you and any officer, director, board
                  member, agent, employee, affiliate of HeartLink, or third
                  party if HeartLink could be liable, directly or indirectly,
                  for such Dispute.&nbsp; This includes any Disputes arising out
                  of or relating to your relationship with us, including without
                  limitation, disputes related to these Terms of Service or the
                  breach, termination, enforcement, interpretation or validity
                  thereof, your use of the services, and/or any rights of
                  privacy and/or publicity.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  During the 60 days from the date you first contacted us to
                  inform us about the Dispute, you and we agree to engage in
                  good faith efforts to resolve the Dispute. You will not
                  initiate any legal action during this period. An extension of
                  time may be mutually agreed upon by you and us.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  Engaging in an informal dispute resolution conference is a
                  requirement that must be fulfilled before commencing
                  arbitration or legal action. The statute of limitations and
                  any filing fee deadlines shall be tolled while the parties
                  engage in the informal dispute resolution process required by
                  this paragraph.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (b)&nbsp; Binding Arbitration Agreement &amp; Class Action
                    Waiver
                  </strong>
                  : YOU AND HeartLink AGREE THAT ANY DISPUTE ARISING OUT OF OR
                  RELATED TO THIS AGREEMENT OR OUR SERVICES IS PERSONAL TO YOU
                  AND HeartLink.&nbsp; YOU AND HeartLink AGREE THAT ANY DISPUTE,
                  CLAIM OR CONTROVERSY MUST BE ARBITRATED ON AN INDIVIDUAL BASIS
                  AND NOT ON A CLASS OR COLLECTIVE BASIS AND NOT IN A COURT OF
                  LAW. YOU AND WE HEREBY EXPRESSLY WAIVE ANY RIGHTS TO SUE IN
                  COURT AND RECEIVE A TRIAL BY JUDGE OR JURY OR TO PARTICIPATE
                  AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION
                  OR REPRESENTATIVE PROCEEDING.&nbsp; The arbitrator, and not
                  any federal, state or local court or agency, shall have
                  exclusive authority to resolve any dispute relating to the
                  interpretation, applicability, enforceability or formation of
                  this Arbitration Agreement, including, but not limited to any
                  claim that all or any part of this Arbitration Agreement is
                  void or voidable.&nbsp; The arbitration proceedings and
                  arbitration outcome are subject to certain confidentiality
                  rules, and judicial review of the arbitration outcome is
                  limited.&nbsp; Discovery and rights to appeal in arbitration
                  are generally more limited than in a lawsuit, and other rights
                  that you and we would have in court may not be available in
                  arbitration. The arbitrator’s award shall be final and binding
                  and may be entered as a judgment in any court of competent
                  jurisdiction. This Agreement to arbitrate such disputes,
                  claims or controversies shall be referred to herein as the
                  “Arbitration Agreement”.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  Notwithstanding the foregoing, the Arbitration Agreement shall
                  not require arbitration of the following Disputes: (i)
                  qualifying individual Disputes in small claims court, so long
                  as such individual action remains in such small claims court
                  and advances only on an individual (non-class,
                  non-representative) basis; (ii) an enforcement action through
                  the applicable federal, state, or local agency if that action
                  is available; or (iii) injunctive or other equitable relief in
                  a court of competent jurisdiction for any dispute related to
                  the actual or threatened infringement or other misuse of
                  intellectual property rights (such as trademarks, trade dress,
                  domain names, trade secrets, copyrights and patents).
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (c)&nbsp; Arbitration Process, Rules, and Forum
                  </strong>
                  : You and HeartLink agree that the terms of this Arbitration
                  Agreement are governed by the Federal Arbitration Act in all
                  respects.&nbsp; If for whatever reason the rules and
                  procedures of the FAA cannot apply, the state law governing
                  arbitration agreements in the state in which you reside shall
                  apply.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  The arbitration will be administered by the American
                  Arbitration Association (“AAA”) under its Consumer Arbitration
                  Rules (collectively, “AAA Rules”), including its Mass
                  Arbitration Supplementary Rules, as modified by these Terms of
                  Service. The AAA Rules and filing forms are available online
                  at www.adr.org.&nbsp; Unless otherwise agreed to by the
                  parties in writing, the arbitration proceedings shall remain
                  confidential.&nbsp; Unless you and HeartLink otherwise agree,
                  or the Mass Arbitration process outlined below is triggered,
                  the arbitration will be conducted in the county where you
                  reside. Any Arbitration Demand filed with the AAA must comply
                  with the requirements set forth by the AAA under its AAA
                  rules. In addition, the demand shall include a statement
                  certifying the completion of the informal dispute resolution
                  conference pursuant to the informal dispute resolution section
                  above.&nbsp; A copy of the Arbitration Demand must be sent by
                  email to the counsel who represented HeartLink in the informal
                  dispute resolution process, or if there was no such counsel
                  then by email to gfmlegal@heart-link.it.com and by mail to
                  Attn: Legal at PO Box 121270, 815 E Street, San Diego, CA,
                  92101, United States.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  If the party requesting arbitration is represented by counsel,
                  the Arbitration Demand shall also include counsel’s name,
                  telephone number, mailing address and email address.&nbsp;
                  Counsel must also sign the Arbitration Demand.&nbsp; By
                  signing the request, counsel certifies to the best of
                  counsel’s knowledge, information, and belief, formed after an
                  inquiry reasonable under the circumstances, that (1) the
                  Arbitration Demand is not being filed for any improper
                  purpose, such as to harass, cause unnecessary delay, or
                  needlessly increase the cost of dispute resolution; (2) the
                  claims, defenses and other legal contentions are warranted by
                  existing law or by a nonfrivolous argument for extending,
                  modifying, or reversing existing law or for establishing new
                  law; and (3) the factual and damages contentions have
                  evidentiary support or, if specifically so identified, we will
                  likely have evidentiary support after a reasonable opportunity
                  for further investigation or discovery.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(d) Arbitration Fees</strong>: Your responsibility to
                  pay any AAA filing fees, case management fees and arbitrator
                  compensation will be solely as set forth in the AAA Rules.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(e) Mass Arbitrations</strong>: In the event 25 or
                  more Arbitration Demands of a similar nature are filed against
                  HeartLink, where representation of all parties is consistent
                  or coordinated across the cases, the AAA Supplementary Rules
                  for Mass Arbitration shall apply.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  All parties agree that Arbitration Demands are of a “similar
                  nature” if they arise out of the same event or similar factual
                  scenario, and raise the same or similar legal issues and seek
                  the same or similar relief. To the extent the parties disagree
                  on the applicability of the Mass Arbitration process, the
                  disagreeing party shall advise the AAA, and the AAA shall
                  appoint a sole standing Process Arbitrator to determine the
                  Mass Arbitration process’ applicability (“Process
                  Arbitrator”). In an effort to expedite resolution of any such
                  disputes, the parties agree the Process Arbitrator may set
                  forth such procedures as are necessary to resolve any disputes
                  promptly.&nbsp; Should initial efforts at dispute resolution
                  through the AAA-ICDR mediation as set forth in Section MA-9 of
                  the AAA Supplementary Rules for Mass Arbitration fail and the
                  cases proceed, you consent to and the Process Arbitrator shall
                  order the cases to proceed according to the following batching
                  terms:
                </p>
                <p style={{ paddingLeft: 80 }}>
                  The Process Arbitrator shall group and administer the
                  arbitration demands into batches of no more than 26 demands
                  per batch (plus, to the extent there are less than 26
                  arbitration demands left over after the batching described
                  above, a final batch consisting of the remaining
                  demands.&nbsp; Thirteen (13) of the initial batch will be
                  selected by the claimants and their coordinated counsel and
                  thirteen (13) will be selected by HeartLink. &nbsp; If there
                  is any dispute on the selection of claimants, the Process
                  Arbitrator will have sole discretion to select the
                  claims.&nbsp; The remaining Arbitration Demands shall be
                  stayed nor shall any arbitration provider fees be assessed in
                  connection with those Arbitration Demands until they are
                  selected to proceed to individual arbitration proceedings as
                  part of the staged process described herein.&nbsp; If the
                  parties are unable to resolve the remaining Arbitration
                  Demands after the conclusion of the initial twenty-six
                  proceedings, the parties shall participate in a second global
                  mediation session. Should this second mediation not result in
                  a global settlement, the batching process will repeat until
                  such resolution is achieved.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  You agree to cooperate in good faith with HeartLink to
                  implement such a batch approach to resolution and fees,
                  including the payment of single case management fees and
                  arbitrator compensation for batches, as well as any steps to
                  minimize the time and costs of arbitration.&nbsp; Any
                  applicable statute of limitations on your Arbitration Demand
                  and filing fee deadlines shall be tolled for batched
                  coordinated disputes from the time any Arbitration Demand is
                  selected for the first set of batching proceedings until the
                  time your Arbitration Demand is selected to proceed in
                  arbitration, withdrawn, or otherwise resolved.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  This Arbitration provision shall in no way be interpreted as
                  authorizing a class, collective and/or mass arbitration or
                  action of any kind, or arbitration involving joint or
                  consolidated claims under any circumstances, except as set
                  forth in this section.&nbsp; If the terms regarding batched
                  disputes is deemed unenforceable as to you or your batch, then
                  it shall be severed and you agree to arbitrate in individual
                  proceedings as ordered by the Process Arbitrator in accordance
                  with this section.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(f)&nbsp; Confidentiality</strong>: We each agree to
                  keep any informal dispute and arbitration proceedings
                  confidential, including all information exchanged between us
                  and any settlement offers, unless otherwise required by law or
                  requested by law enforcement or any court or governmental
                  body. However, we may each disclose these matters, in
                  confidence, to our respective attorneys, accountants,
                  auditors, and insurance providers.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(g)&nbsp; Enforceability</strong>: If any provision of
                  these Terms of Service or this Dispute Resolution section is
                  found to be unenforceable, illegal or invalid for any reason,
                  such unenforceability, illegality or invalidity will not
                  affect any other provision of these Terms of Service or this
                  Dispute Resolution section, and these Terms of Service and
                  this Dispute Resolution section will be construed as if such
                  unenforceable, illegal or invalid provision had never been
                  contained therein.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(h)&nbsp; Opt Out</strong>:&nbsp; You may opt out of
                  this Arbitration Agreement. To opt out, you must notify
                  HeartLink in writing no later than 30 days after first
                  becoming subject to this Arbitration Agreement including any
                  updates to the Arbitration Agreement.&nbsp; Your notice must
                  include your name and address, the title of and link to your
                  HeartLink fundraiser (if any), the email address you use to
                  access your HeartLink Account or Supporter Account (if you
                  have one), and a CLEAR statement that you want to opt out of
                  this Arbitration Agreement.&nbsp; You must send your opt-out
                  notice to gfmlegal@heart-link.it.com&nbsp; with the subject
                  line “Arbitration Opt-Out Notice”.&nbsp; If you opt out of
                  this Arbitration Agreement, all other parts of these Terms of
                  Service will continue to apply to you. HeartLink will continue
                  to honor the valid opt outs of users who validly opted out of
                  the Arbitration Agreement in a prior version of the Terms of
                  Service.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>(i)&nbsp; Time Period for Claims</strong>: You agree
                  that&nbsp; any claim or cause of action arising out of or
                  related to use of the Services, the Platform or these Terms of
                  Service must be filed within one (1) year after such claim or
                  cause of action arose or otherwise will be forever barred,
                  which means that you and HeartLink will not have a right to
                  assert the Claim.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    19.2. USERS IN THE UK, EUROPEAN ECONOMIC AREA AND
                    SWITZERLAND
                  </strong>
                  :&nbsp; These Terms and any dispute arising out of or in
                  connection with your use of the Services are governed by the
                  laws of the Republic of Ireland subject to any more favorable
                  public policy provisions under your local law, and the courts
                  of the Republic of Ireland have exclusive jurisdiction,&nbsp;
                  except to the extent that public policy provision under your
                  local law allows for the competence of your local courts.
                </p>
                <p style={{ paddingLeft: 80 }}>
                  <strong>
                    (a)&nbsp; Users In The European Union, Norway, Iceland Or
                    Liechtenstein
                  </strong>
                  : You have the right to submit all unresolved disputes between
                  you and HeartLink to the Online Dispute Resolution platform
                  (ODR) arbitration administered by the European Union:{" "}
                  <Link onClick={toTop} to="/">
                    https://consumer-redress.ec.europa.eu/index_en
                  </Link>
                  &nbsp;then in effect, except that either party may seek
                  injunctive relief for infringement of intellectual property
                  rights or other proprietary rights, in court and that claim
                  brought under the EU or UK General Data Protection Regulations
                  may be resolved locally in court or by referral to a competent
                  privacy regulator.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>19.3. Disputes With Other Users</strong>:&nbsp; You
                  agree that you are solely responsible for your interactions
                  with any other User in connection with the Services and
                  neither HeartLink nor its Affiliates will have any liability
                  or responsibility with respect thereto. HeartLink and its
                  Affiliates reserve the right, but have no obligation, to
                  become involved in any way with disputes between you and any
                  other User of the Services.
                </p>
                <h2 id="h2-number-20">20. Changes to the Terms</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>
                    20.1. Policies regarding how changes to the Terms of Service
                    will be communicated and implemented
                  </strong>
                  :&nbsp; We reserve the right, at our sole discretion, to
                  change or modify portions of these Terms of Service at any
                  time. Where possible we will provide 30 days’ notice of
                  substantive changes to these Terms of Service and, if
                  appropriate, we may inform Users of such changes via email or
                  other appropriate means. Changes may be made at short or no
                  notice where a change is required by law. When we make a
                  change, we will post the revised Terms of Service on this page
                  and will indicate the date of such revision. Your continued
                  use of the Services after the date of any such changes
                  constitutes your acceptance of the new Terms of Service. If
                  you do not wish to accept the new Terms of Service, you should
                  discontinue your use of the Services.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>20.2. English Version Binding</strong>:&nbsp; To the
                  extent allowed by applicable law, the English language version
                  of these Terms of Service is binding and their translations in
                  other languages are for convenience only; in case of
                  discrepancies between the English version of these Terms of
                  Service and their translations, the English version shall
                  prevail.
                </p>
                <h2 id="h2-number-21">21. Miscellaneous</h2>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.1. Entire Agreement</strong>:&nbsp; These Terms of
                  Service constitute the entire agreement between you and
                  HeartLink and govern your use of the Services and the
                  Platform, superseding any prior agreements between you and
                  HeartLink with respect to the Services. You also may be
                  subject to additional terms of service that may apply when you
                  use affiliate or third-party services, third-party content or
                  third-party software.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.2. Governing Law</strong>:&nbsp; For Users in the
                  United Kingdom, European Economic Area and Switzerland, these
                  Terms of Service and any dispute arising out of or in
                  connection with your use of the Services are governed as set
                  out above under “Disputes – Users In the UK, European Economic
                  Area and Switzerland”.&nbsp; For all other Users, these Terms
                  of Service will be governed by the laws of the State of
                  California without regard to its conflict of law provisions.
                  With respect to any disputes or claims not subject to
                  arbitration, as set forth above, you and HeartLink agree to
                  submit to the personal and exclusive jurisdiction of the state
                  and federal courts located within San Mateo County,
                  California.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.3. Waiver</strong>:&nbsp; The failure of HeartLink
                  to exercise or enforce any right or provision of these Terms
                  of Service will not constitute a waiver of such right or
                  provision.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.4. Severability</strong>:&nbsp; If any provision of
                  these Terms of Service is found by a court of competent
                  jurisdiction to be (or are otherwise) invalid, the parties
                  nevertheless agree that the court should endeavor to give
                  effect to the parties’ intentions as reflected in the
                  provision, and the other provisions of these Terms of Service
                  remain in full force and effect.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.5. Printed Version</strong>:&nbsp; A printed
                  version of this agreement and of any notice given in
                  electronic form will be admissible in judicial or
                  administrative proceedings based upon or relating to this
                  agreement to the same extent and subject to the same
                  conditions as other business documents and records originally
                  generated and maintained in printed form.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.6. Assignment</strong>:&nbsp; You may not assign
                  these Terms of Service without the prior written consent of
                  HeartLink, but HeartLink and/or its Affiliates may assign or
                  transfer these Terms of Service, in whole or in part, without
                  restriction. For the sake of clarity, HeartLink and/or its
                  Affiliates may, at any time, assign our rights or delegate our
                  obligations hereunder without notice to you in connection with
                  a merger, acquisition, reorganization or sale of equity or
                  assets, or by operation of law or otherwise.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.7. Section Titles</strong>: The section titles in
                  these Terms of Service are for convenience only and have no
                  legal or contractual effect.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.8. Notices</strong>:&nbsp; To the extent permitted
                  by applicable law, notices to you may be made via email or
                  regular mail. The Services may also provide notices to you of
                  changes to these Terms of Service or other matters by
                  displaying notices or links to notices generally on the
                  Platform.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.9. Force Majeure</strong>:&nbsp; Except to the
                  extent provided otherwise under applicable law,&nbsp;
                  HeartLink shall not be liable for any delay or failure to
                  perform resulting from causes outside its reasonable control,
                  including, but not limited to, acts of God, war or threats of
                  war, terrorism or threats of terrorism, riots, embargos, acts
                  of civil or military authorities, fire, floods, accidents,
                  governmental regulation or advisory, recognized health
                  threats, as determined by the World Health Organization, the
                  Centers for Disease Control, or local government authority or
                  health agencies, strikes or shortages or curtailment of
                  transportation facilities, fuel, energy, labor or materials.
                </p>
                <p style={{ paddingLeft: 40 }}>
                  <strong>21.10. Export Controls</strong>:&nbsp; Software
                  available in connection with the Services and the transmission
                  of applicable data, if any, may be subject to export controls
                  and economic sanctions laws of the United States or other
                  jurisdictions. No Software may be downloaded from the Services
                  or otherwise exported or re-exported in violation of such
                  export control and economic sanctions laws. Downloading or
                  using the Software is at your sole risk. You represent and
                  warrant that (i) you are not located in a country that is
                  subject to a U.S. Government embargo, or that has been
                  designated by the U.S. Government as a “terrorist supporting”
                  country; (ii) you are not listed on any U.S. Government list
                  of prohibited or restricted parties and (iii) you are not
                  located in any other country or jurisdiction from which you
                  would be barred from using the Services by applicable law.
                </p>
                <h2 id="h2-number-22">22. Questions? Concerns? Suggestions?</h2>
                <p>
                  Please visit the{" "}
                  <Link onClick={toTop} to="/">
                    Help Center
                  </Link>{" "}
                  to learn more about HeartLink’s Services and Platform or
                  contact us to report violations or pose any question.
                </p>
                <hr />
                <p>
                  <strong>HeartLink Inc., ℅ Legal Department</strong>
                  <br />
                  <strong>PO Box 121270, 815 E Street&nbsp;</strong>
                  <br />
                  <strong>San Diego, CA, 92101, United States</strong>
                </p>
                <p>
                  <strong>
                    HeartLink Ireland, Limited – Company Number: 581748, VAT
                    Number: 3417986GH
                  </strong>
                  <br />
                  <strong>
                    70 Sir John Rogerson’s Quay, Dublin 2, Ireland, D02R296
                  </strong>
                </p>
                <p>
                  <b>
                    HeartLink International Services UK Ltd- Company Number
                    16576719.
                    <br />
                  </b>
                  <b style={{ fontSize: "1rem", letterSpacing: 0 }}>
                    Rutland House, 148 Edmund St. Birmingham, B32JR
                  </b>
                </p>
                <p>
                  <strong>HeartLink Australia PTY Ltd.,</strong>
                  <br />
                  <strong>
                    Tower One – International Towers Sydney, Level 46
                  </strong>
                  <br />
                  <strong>100 Barangaroo Avenue, 2000, Sydney, NSW.</strong>
                </p>
              </div>
              <div className="sidebar">
                <div className="sidebar-inner">
                  <div
                    ref={linkListRef}
                    className={`link-list custom-links-false ${
                      tocOpen ? "open" : "collapsed"
                    }`}
                  >
                    <div className="sidebar-title">
                      <button
                        type="button"
                        className="toc-toggle"
                        onClick={() => {
                          setTocOpen((v) => !v);
                          // trigger recalc/scroll handlers after DOM updates so fixed state updates immediately
                          window.setTimeout(() => {
                            window.dispatchEvent(new Event("resize"));
                            window.dispatchEvent(new Event("scroll"));
                          }, 0);
                        }}
                        aria-expanded={tocOpen}
                        aria-controls="terms-toc-list"
                      >
                        <span>Table of Contents</span>
                        <span className="toc-icon" aria-hidden="true">
                          {tocOpen ? "−" : "+"}
                        </span>
                      </button>
                    </div>
                    <ul id="terms-toc-list" className="toc-list">
                      <li>
                        <a href="#h2-number-1">1. Introduction</a>
                      </li>
                      <li>
                        <a href="#h2-number-2">2. Definitions</a>
                      </li>
                      <li>
                        <a href="#h2-number-3">3. The Services We Provide</a>
                      </li>
                      <li>
                        <a href="#h2-number-4">
                          4. Account Creation and Eligibility Requirements
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-5">5. Payment Processors</a>
                      </li>
                      <li>
                        <a href="#h2-number-6">
                          6. User Responsibilities and Obligations
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-7">
                          7. Transfers, Holds, Returns &amp; Chargebacks
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-8">
                          8. Prohibited Fundraisers and Related User Content
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-9">9. Prohibited User Conduct</a>
                      </li>
                      <li>
                        <a href="#h2-number-10">
                          10. Content Moderation and Reporting Fundraisers
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-11">11. Fees</a>
                      </li>
                      <li>
                        <a href="#h2-number-12">
                          12. Intellectual Property Rights, Content Ownership
                          and Licenses
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-13">13. Data Privacy</a>
                      </li>
                      <li>
                        <a href="#h2-number-14">14. Artificial Intelligence</a>
                      </li>
                      <li>
                        <a href="#h2-number-15">
                          15. Third-Party Services and Content
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-16">
                          16. Suspension or Termination of Accounts
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-17">
                          17. Disclaimers and Limitations of Liability
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-18">
                          18. Indemnification and Release
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-19">
                          19. Dispute Resolution &amp; Arbitration
                        </a>
                      </li>
                      <li>
                        <a href="#h2-number-20">20. Changes to the Terms</a>
                      </li>
                      <li>
                        <a href="#h2-number-21">21. Miscellaneous</a>
                      </li>
                      <li>
                        <a href="#h2-number-22">
                          22. Questions? Concerns? Suggestions?
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="sidebar-cta">
                    <Link
                      onClick={toTop}
                      className="hrt-secondary-button hrt-secondary-button--gray hrt-secondary-button--full-for-small hrt-secondary-button--large hrt-base-button"
                      to="/"
                    >
                      Start a HeartLink
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <p />
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
